import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { filterMrpWarehouseList, resolveMrpWarehouseDisplayLocation } from '../utils/mrpWarehouseFilter';
import * as XLSX from 'xlsx';

const STEPS = [
  { num: 1, label: 'Scenario' },
  { num: 2, label: 'Scenario Details' },
  { num: 3, label: 'Item Selection' },
  { num: 4, label: 'Data Source' },
  { num: 5, label: 'Documents' },
  { num: 6, label: 'Results' },
];

const DEFAULT_DOCSOURCE = {
  timeRange: 'Within Planning Horizon',
  // demand/supply document toggles
  purchaseRequests: false,       restrictPurchaseRequests: false,
  purchaseQuotations: false,     restrictPurchaseQuotations: false,
  purchaseOrders: true,          restrictPurchaseOrders: false,
  blanketPurchaseAgreements: true, restrictPurchaseAgreements: false,
  salesQuotations: false,        restrictSalesQuotations: false,
  salesOrders: true,             restrictSalesOrders: false,
  blanketSalesAgreements: true,  restrictSalesAgreements: false,
  productionOrders: true,        restrictProductionOrders: false,
  inventoryTransferRequests: false, restrictTransferRequests: false,
  recurringOrderTransactions: false,
  reserveInvoices: false,        restrictReserveInvoices: false,
  // restrict date ranges (from/to for each restrict)
  restrictDates: {},
  // inventory & forecast
  inventoryLevel: 'Item Cost',
  forecast: '',
  // recommendations
  recPurchase: true,
  purchaseRecType: 'Purchase Orders',
  purchaseWarehouse: 'Generate to Warehouse with the Demand',
  recProductionOrders: true,
  // On by default: the client's SAP scenario always ticks it, and off turns every
  // transfer into a purchase recommendation.
  recInventoryTransfers: true,
};

const DEFAULT_FORM = { runName: '', scenarioDesc: '', projectNumber: '', startDate: '', endDate: '' };
/** Step 3: page size for item table (project / selected / all items) — keeps DOM small. */
const STEP_SCOPE_PAGE_SIZE = 100;

/** Step 3: third option loads item list from sales orders matching bp_project or so_number */
const SCOPE_BY_SALES_PROJECT = 'By project code (sales order)';
const DEFAULT_DETAILS = {
  periodQty: '1', periodUnit: 'Months', horizonLength: '6',
  holidaysProduction: true, holidaysPurchase: true,
  ignoreCumulativeLeadTime: false,
  sortBy: 'Assembly Sequence',
  displayNoRequirements: false, displaySelectedOnly: false, simulation: false,
  // openPO defaults OFF: until now the engine's open-PO netting read only POs
  // raised inside this app (3 on production), so ticking it changed nothing. It
  // now nets SAP's real incoming supply, which on SO 262700295 removed 137 of 539
  // recommendations — and SAP's own MRP did not net that supply in the client's
  // scenario. Left on by default it would silently move results away from SAP.
  openPO: false, forecasts: true, safety: '1.0',
};

export default function MRPWizard() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(1);
  const [form, setForm]       = useState(DEFAULT_FORM);
  const [details, setDetails] = useState(DEFAULT_DETAILS);
  const [scope, setScope]     = useState({
    mode: 'All Items',
    selectedItems: [],
    projectSalesOrderIds: [],
  });
  const [dataSource, setDataSource] = useState({ runBy: 'Company', warehouses: [] });
  /**
   * What has changed in this sales order since it was last planned?
   *
   * The server compares what the last run planned from with the same reading today
   * (/mrp/run-eligibility). It is shown when there is something to report; planning
   * an unchanged order again is allowed, and is the planner's call.
   */
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [docSource, setDocSource]   = useState(DEFAULT_DOCSOURCE);
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [holidays, setHolidays] = useState(null);
  const [scenarioMode, setScenarioMode] = useState('create');

  /* Existing Scenarios state */
  const [scenarios, setScenarios]           = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [scenarioSearch, setScenarioSearch]     = useState('');

  /* Fetch existing MRP scenarios on mount */
  useEffect(() => {
    setLoadingScenarios(true);
    api.get('/mrp/runs?latest=1')
      .then(data => {
        const rows = Array.isArray(data) ? data : data?.rows || [];
        setScenarios(rows);
      })
      .catch(() => setScenarios([]))
      .finally(() => setLoadingScenarios(false));
  }, []);

  /* Fetch holidays whenever planning dates change */
  const fetchHolidays = useCallback(async () => {
    if (!form.startDate || !form.endDate) { setHolidays(null); return; }
    try {
      const data = await api.get(`/mrp/holidays?start_date=${form.startDate}&end_date=${form.endDate}`);
      setHolidays(data);
    } catch { setHolidays(null); }
  }, [form.startDate, form.endDate]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const reset = () => { setForm(DEFAULT_FORM); setDetails(DEFAULT_DETAILS); setDocSource(DEFAULT_DOCSOURCE); setResult(null); setError(''); setStep(1); setScope({ mode: 'All Items', selectedItems: [], projectSalesOrderIds: [] }); setDataSource({ runBy: 'Company', warehouses: [] }); setSelectedScenario(null); setScenarioSearch(''); setScenarioMode('create'); setProjectInfo(null); };

  // A partial run counts as a change: the user is planning something different.
  const isPartialRun = Array.isArray(scope.selectedSoLineItemIds) && scope.selectedSoLineItemIds.length > 0;

  const canNext = step === 1 ? (scenarioMode === 'create' ? !!form.runName : !!selectedScenario)
                : step === 2 ? !!(form.startDate && form.endDate)
                : step === 3 ? (scope.mode === 'All Items' || scope.selectedItems.length > 0)
                : true;

  useEffect(() => {
    const soNumber = selectedScenario
      ? (selectedScenario.so_numbers || (selectedScenario.row_type === 'so' ? selectedScenario.run_name : null))
      : null;
    if (scenarioMode !== 'existing' || !soNumber) { setEligibility(null); return undefined; }
    let cancelled = false;
    setEligibilityLoading(true);
    api.get(`/mrp/run-eligibility?so_number=${encodeURIComponent(String(soNumber).trim())}`)
      .then((d) => {
        if (cancelled) return;
        setEligibility(d || null);
        applyLastRunSettings(d?.last_settings);
      })
      .catch(() => { if (!cancelled) setEligibility(null); })
      .finally(() => { if (!cancelled) setEligibilityLoading(false); });
    return () => { cancelled = true; };
  }, [selectedScenario, scenarioMode]);

  /**
   * Reopen the data source and document steps on last time's answers.
   *
   * A planner re-running an order had to tick the same six warehouses and the same
   * document sources again every time. The run records what was chosen, so choosing
   * that order again restores it — and anything the planner then changes wins, since
   * this only runs when the order is picked.
   */
  const applyLastRunSettings = useCallback((saved) => {
    if (!saved || typeof saved !== 'object') return;

    if (saved.run_by || Array.isArray(saved.inventory_wh_ids)) {
      setDataSource((ds) => {
        const inventory = new Set((saved.inventory_wh_ids || []).map(Number));
        const production = new Set((saved.production_wh_ids || []).map(Number));
        const inScope = new Set((saved.warehouse_ids || []).map(Number));
        return {
          ...ds,
          runBy: saved.run_by || ds.runBy,
          // Warehouses load on step 4; tick them there if they are not loaded yet.
          warehouses: (ds.warehouses || []).map((w) => {
            const id = Number(w.warehouse_id);
            return {
              ...w,
              inclInventory: inventory.size ? inventory.has(id) : w.inclInventory,
              inclProduction: production.size ? production.has(id) : w.inclProduction,
              inclDemand: inScope.size ? inScope.has(id) || inventory.has(id) : w.inclDemand,
              inclSupply: inScope.size ? inScope.has(id) || inventory.has(id) : w.inclSupply,
            };
          }),
          savedWarehouses: {
            inventory: [...inventory],
            production: [...production],
            inScope: [...inScope],
          },
        };
      });
    }

    setDocSource((prev) => ({
      ...prev,
      purchaseRequests: saved.include_purchase_requests ?? prev.purchaseRequests,
      purchaseQuotations: saved.include_purchase_quotations ?? prev.purchaseQuotations,
      purchaseOrders: saved.include_purchase_orders ?? prev.purchaseOrders,
      blanketPurchaseAgreements: saved.include_blanket_purchase ?? prev.blanketPurchaseAgreements,
      salesQuotations: saved.include_sales_quotations ?? prev.salesQuotations,
      salesOrders: saved.include_sales_orders ?? prev.salesOrders,
      blanketSalesAgreements: saved.include_blanket_sales ?? prev.blanketSalesAgreements,
      productionOrders: saved.include_production_orders ?? prev.productionOrders,
      inventoryTransferRequests: saved.include_transfer_requests ?? prev.inventoryTransferRequests,
      recurringOrderTransactions: saved.include_recurring_orders ?? prev.recurringOrderTransactions,
      reserveInvoices: saved.include_reserve_invoices ?? prev.reserveInvoices,
      restrictPurchaseOrders: saved.restrict_purchase_orders ?? prev.restrictPurchaseOrders,
      restrictSalesOrders: saved.restrict_sales_orders ?? prev.restrictSalesOrders,
      restrictProductionOrders: saved.restrict_production_orders ?? prev.restrictProductionOrders,
      restrictDates: saved.restrict_dates ?? prev.restrictDates,
      inventoryLevel: saved.inventory_level ?? prev.inventoryLevel,
      forecast: saved.forecast_mode ?? prev.forecast,
      timeRange: saved.time_range ?? prev.timeRange,
      purchaseRecType: saved.purchase_rec_type ?? prev.purchaseRecType,
      purchaseWarehouse: saved.purchase_warehouse ?? prev.purchaseWarehouse,
      recInventoryTransfers: saved.rec_inventory_transfers ?? prev.recInventoryTransfers,
    }));
  }, []);

  const handleRun = async () => {
    setRunning(true); setError('');
    try {
      const autoName = scenarioMode === 'existing' && selectedScenario
        ? selectedScenario.run_name
        : form.runName;
      const data = await api.post('/mrp/run', {
        run_name:   autoName,
        description: scenarioMode === 'existing' && selectedScenario ? (selectedScenario.description || autoName) : (form.scenarioDesc || autoName),
        project_number: scenarioMode === 'existing' && selectedScenario ? (selectedScenario.project_number || null) : (form.projectNumber || null),
        start_date: form.startDate,
        end_date:   form.endDate,
        // Step 2 scenario details
        period_qty:                   parseInt(details.periodQty) || 1,
        period_unit:                  details.periodUnit,
        horizon_length:               parseInt(details.horizonLength) || 6,
        consider_holidays_production: details.holidaysProduction,
        consider_holidays_purchase:   details.holidaysPurchase,
        ignore_cumulative_lead_time:  details.ignoreCumulativeLeadTime,
        sort_by:                      details.sortBy,
        display_no_requirements:      details.displayNoRequirements,
        display_selected_only:        details.displaySelectedOnly,
        simulation:                   details.simulation,
        // Step 2 data-source toggles
        safety_factor:    parseFloat(details.safety) || 1.0,
        consider_open_po: details.openPO,
        consider_forecasts: details.forecasts,
        // Step 3 item scope (project option submits as Selected Items + SO ids for demand)
        item_scope: scope.mode === SCOPE_BY_SALES_PROJECT ? 'Selected Items' : scope.mode,
        selected_items:
          scope.mode === 'Selected Items' || scope.mode === SCOPE_BY_SALES_PROJECT
            ? scope.selectedItems.map((i) => i.item_id)
            : [],
        // Step 4 data source
        run_by:       dataSource.runBy,
        // The server reads warehouse_ids as the warehouses whose stock MRP may net and
        // transfer from — SAP's "Include Existing Inventory" column. It used to receive
        // every warehouse with any column ticked, so the client's standard scenario
        // (inventory in six warehouses, demand in all of them) became "take stock from
        // all of them": on SO 262701072 that pulled transfers out of 18 warehouses SAP
        // had been told to ignore, and 21 purchase orders SAP raised disappeared.
        // Demand warehouses need no list: the server takes them from the sales order
        // lines and BOMs themselves. A run with no inventory column ticked at all keeps
        // the old behaviour rather than silently netting nothing.
        warehouse_ids: (dataSource.warehouses.some(w => w.inclInventory)
          ? dataSource.warehouses.filter(w => w.inclInventory)
          : dataSource.warehouses.filter(w => w.inclInventory || w.inclDemand || w.inclSupply))
          .map(w => w.warehouse_id),
        // And ONLY their stock counts — exactly SAP's rule. Stock sitting in a demand
        // warehouse whose inventory box is unticked is invisible to SAP's run, so SAP
        // buys or transfers again; we used to count it and recommend nothing. On SO
        // 262701107 that was the whole gap: 281/285 counting WH20's stock, 285/285
        // ignoring it, same stock, same minute.
        inventory_wh_ids: dataSource.warehouses
          .filter(w => w.inclInventory)
          .map(w => w.warehouse_id),
        // Whose open production orders count as supply. A shop order finishing into
        // WH02 is not a receipt due into WH01, so this is ticked apart from Include
        // Supply. Unticking every warehouse means none of them count.
        production_wh_ids: dataSource.warehouses
          .filter(w => w.inclProduction)
          .map(w => w.warehouse_id),
        // Step 5 document sources
        include_purchase_requests:  docSource.purchaseRequests,
        include_purchase_quotations: docSource.purchaseQuotations,
        include_purchase_orders:    docSource.purchaseOrders,
        include_blanket_purchase:   docSource.blanketPurchaseAgreements,
        include_sales_quotations:   docSource.salesQuotations,
        include_sales_orders:       docSource.salesOrders,
        include_blanket_sales:      docSource.blanketSalesAgreements,
        include_production_orders:  docSource.productionOrders,
        include_transfer_requests:  docSource.inventoryTransferRequests,
        include_recurring_orders:   docSource.recurringOrderTransactions,
        include_reserve_invoices:   docSource.reserveInvoices,
        // restrict flags + date ranges
        restrict_purchase_requests:   docSource.restrictPurchaseRequests,
        restrict_purchase_quotations: docSource.restrictPurchaseQuotations,
        restrict_purchase_orders:     docSource.restrictPurchaseOrders,
        restrict_purchase_agreements: docSource.restrictPurchaseAgreements,
        restrict_sales_quotations:    docSource.restrictSalesQuotations,
        restrict_sales_orders:        docSource.restrictSalesOrders,
        restrict_sales_agreements:    docSource.restrictSalesAgreements,
        restrict_production_orders:   docSource.restrictProductionOrders,
        restrict_transfer_requests:   docSource.restrictTransferRequests,
        restrict_reserve_invoices:    docSource.restrictReserveInvoices,
        restrict_dates:               docSource.restrictDates || {},
        // inventory & forecast
        inventory_level:            docSource.inventoryLevel,
        forecast_mode:              docSource.forecast,
        time_range:                 docSource.timeRange,
        // recommendations
        rec_purchase:               true,
        purchase_rec_type:          docSource.purchaseRecType,
        purchase_warehouse:         docSource.purchaseWarehouse,
        rec_production_orders:      true,
        rec_inventory_transfers:    docSource.recInventoryTransfers,
        // Sales Scenarios - selected scenario run ID
        // Ticked SO lines for a partial run; null means the whole order.
        selected_so_line_item_ids:  scope.selectedSoLineItemIds || null,
        selected_scenario_id:       selectedScenario?.run_id || null,
        selected_sales_order_ids: (() => {
          const fromScenario =
            selectedScenario?.row_type === 'so' && selectedScenario.so_id != null
              ? [Number(selectedScenario.so_id)]
              : [];
          const fromProject =
            scope.mode === SCOPE_BY_SALES_PROJECT && Array.isArray(scope.projectSalesOrderIds)
              ? scope.projectSalesOrderIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
              : [];
          return [...new Set([...fromScenario, ...fromProject])];
        })(),
      });
      setResult(data);
      setStep(6);
    } catch (e) {
      setError(e.message || 'MRP run failed. Please check server connection.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-bold text-sap-text">MRP Wizard</h1>
          <p className="text-xs text-sap-textLight mt-0.5">Material Requirements Planning</p>
        </div>
        <button onClick={reset}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold border border-sap-border
                     rounded-lg hover:bg-sap-blueLight text-sap-blue transition-all">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round">
            <polyline points="1 4 1 10 7 10"/>
            <path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
          </svg>
          New Scenario
        </button>
      </div>

      <div className="card">
        {/* Stepper */}
        <div className="flex border-b border-sap-border bg-sap-bg">
          {STEPS.map((s) => (
            <button key={s.num} onClick={() => step > s.num && setStep(s.num)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs transition-colors
                ${step === s.num
                  ? 'bg-white text-sap-blue font-semibold border-b-2 border-sap-blue'
                  : step > s.num
                    ? 'text-sap-success cursor-pointer hover:bg-white/60'
                    : 'text-sap-textLight cursor-default'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-2xs font-bold shrink-0
                ${step === s.num ? 'bg-sap-blue text-white'
                  : step > s.num ? 'bg-sap-success text-white' : 'bg-[#DDD9D1] text-[#6B7F7F]'}`}>
                {step > s.num ? '✓' : s.num}
              </span>
              <span className="hidden sm:inline">{s.label}</span>
            </button>
          ))}
        </div>

        {/* Step content */}
        <div className="p-6 min-h-[280px]">
          {step === 1 && <StepScenario       form={form}       setForm={setForm}
                                              scenarios={scenarios} loadingScenarios={loadingScenarios}
                                              scenarioSearch={scenarioSearch} setScenarioSearch={setScenarioSearch}
                                              selectedScenario={selectedScenario} setSelectedScenario={setSelectedScenario}
                                              scenarioMode={scenarioMode} setScenarioMode={setScenarioMode}
                                              eligibility={eligibility} eligibilityLoading={eligibilityLoading}
                                              isPartialRun={isPartialRun} />}
          {step === 2 && <StepScenarioDetails form={form} setForm={setForm} details={details} setDetails={setDetails} holidays={holidays} navigate={navigate} scenarioMode={scenarioMode} />}
          {step === 3 && <StepScope           scope={scope}     setScope={setScope} />}
          {step === 4 && <StepDataSource      dataSource={dataSource} setDataSource={setDataSource} />}
          {step === 5 && <StepDocSource       docSource={docSource} setDocSource={setDocSource}
                                              running={running} error={error} onRun={handleRun} />}
          {step === 6 && <StepResults         result={result} form={form} details={details} />}
        </div>

        {/* Footer nav */}
        <div className="px-6 py-4 border-t border-sap-border bg-sap-bg flex items-center justify-between">
          <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
            className="px-4 py-2 text-xs font-medium border border-sap-border rounded-lg hover:bg-white
                       disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            ← Back
          </button>
          {step < 5 && (
            <button onClick={() => setStep(s => s + 1)} disabled={!canNext}
              className="px-5 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg
                         hover:bg-sap-blueDark disabled:opacity-40 shadow-sm transition-all">
              Next →
            </button>
          )}
          {step === 6 && (
            <button onClick={reset}
              className="px-5 py-2 text-xs font-semibold border border-sap-blue text-sap-blue
                         rounded-lg hover:bg-sap-blueLight transition-all">
              New Scenario
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Steps ─────────────────────────────────────── */

/** Already planned, and nothing has changed — say so, and say what would unlock it. */
function RerunNotice({ eligibility, loading, isPartialRun, compact }) {
  if (loading) return <p className="text-2xs text-sap-textLight mt-2">Checking whether this order changed since its last run…</p>;
  if (!eligibility) return null;
  const { can_run: canRun, last_run: lastRun, changes = [], message } = eligibility;
  const fmt = (v) => (v ? new Date(v).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  }) : null);

  // An order that has not changed can still be planned again — say nothing.
  if (!canRun && !isPartialRun) return null;

  if (!canRun && isPartialRun) {
    return (
      <div className="mt-3 rounded-lg border border-sap-blue/30 bg-sap-blue/5 px-3 py-2.5">
        <p className="text-xs font-semibold text-sap-blueDeeper">Partial run</p>
        <p className="text-2xs text-sap-text mt-1">
          This order was already planned{lastRun ? ` (run #${lastRun.run_id} on ${fmt(lastRun.created_at)})` : ''},
          but you have selected part of it, so this run is allowed.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5">
      <p className="text-xs font-semibold text-green-900">
        {lastRun ? 'Changed since the last run — can be planned again' : 'Not planned yet'}
      </p>
      <p className="text-2xs text-green-900/90 mt-1">{message}</p>
      {changes.length > 0 && !compact && (
        <ul className="mt-1.5 space-y-0.5 text-2xs text-green-900/90 max-h-32 overflow-y-auto">
          {changes.map((c, i) => (
            <li key={i}>• {c.text}{c.at ? ` (${fmt(c.at)})` : ''}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StepScenario({ form, setForm, scenarios, loadingScenarios, scenarioSearch, setScenarioSearch, selectedScenario, setSelectedScenario, scenarioMode, setScenarioMode, eligibility, eligibilityLoading, isPartialRun }) {
  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  const filtered = scenarios.filter(sc =>
    !scenarioSearch ||
    (sc.run_name || '').toLowerCase().includes(scenarioSearch.toLowerCase()) ||
    (sc.description || '').toLowerCase().includes(scenarioSearch.toLowerCase()) ||
    (sc.project_number || '').toLowerCase().includes(scenarioSearch.toLowerCase()) ||
    (sc.so_numbers || '').toLowerCase().includes(scenarioSearch.toLowerCase()) ||
    (sc.created_by || '').toLowerCase().includes(scenarioSearch.toLowerCase())
  );

  const selectScenario = (sc) => {
    setSelectedScenario(prev => prev?.run_id === sc.run_id && prev?.so_id === sc.so_id ? null : sc);
    // Pre-fill form dates from selected scenario / SO
    const isSame = selectedScenario && selectedScenario.run_id === sc.run_id && selectedScenario.so_id === sc.so_id;
    if (!isSame) {
      setForm(f => ({
        ...f,
        startDate: sc.start_date ? sc.start_date.slice(0, 10) : f.startDate,
        endDate:   sc.end_date ? sc.end_date.slice(0, 10) : f.endDate,
      }));
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="page-title">Scenario Selection</p>
        <p className="text-xs text-sap-textLight mt-0.5">To run an existing scenario, select "Select Existing Scenario". If you select "Create New Scenario", enter a unique name for the scenario.</p>
      </div>

      {/* Radio Buttons */}
      <div className="flex items-center gap-8">
        <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer font-medium">
          <input type="radio" name="scenarioMode" value="create"
            checked={scenarioMode === 'create'}
            onChange={() => setScenarioMode('create')}
            className="accent-sap-blue w-3.5 h-3.5" />
          Create New Scenario
        </label>
        <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer font-medium">
          <input type="radio" name="scenarioMode" value="existing"
            checked={scenarioMode === 'existing'}
            onChange={() => setScenarioMode('existing')}
            className="accent-sap-blue w-3.5 h-3.5" />
          Select Existing Scenario
        </label>
      </div>

      {/* Create New Scenario */}
      {scenarioMode === 'create' && (
        <div className="border border-sap-border rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-sap-bg border-b border-sap-border">
            <span className="text-xs font-semibold text-sap-blue">New Scenario</span>
          </div>
          <div className="px-4 py-4 space-y-3 max-w-lg">
            <div className="flex items-center gap-3">
              <label className="w-36 text-xs font-medium text-sap-textLight text-right shrink-0">Scenario Name *</label>
              <input type="text" value={form.runName} onChange={e => s('runName', e.target.value)}
                placeholder="e.g. S00001 April 2026"
                className="flex-1 h-8 px-3 text-xs border border-sap-border rounded-lg bg-white
                           focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
            </div>
            <div className="flex items-start gap-3">
              <label className="w-36 text-xs font-medium text-sap-textLight text-right shrink-0 pt-2">Description</label>
              <textarea value={form.scenarioDesc} onChange={e => s('scenarioDesc', e.target.value)}
                placeholder="Optional description for this scenario…"
                rows={3}
                className="flex-1 px-3 py-2 text-xs border border-sap-border rounded-lg bg-white resize-none
                           focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
            </div>
          </div>
        </div>
      )}

      {/* Select Existing Scenario */}
      {scenarioMode === 'existing' && (
        <div className="border border-[#8a9ba7] overflow-hidden">
          {/* Search Bar */}
          <div className="px-3 py-2 bg-[#f4f6f8] border-b border-[#c0c0c0] flex items-center gap-2">
            <svg className="w-3.5 h-3.5 text-[#888] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/>
            </svg>
            <input
              type="text"
              value={scenarioSearch}
              onChange={e => setScenarioSearch(e.target.value)}
              placeholder="Search by scenario name, description, project no., SO number…"
              className="flex-1 bg-white h-7 px-2.5 text-xs border border-[#c0c0c0] rounded focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/30"
            />
            {scenarioSearch && (
              <button onClick={() => setScenarioSearch('')} className="text-[#888] hover:text-[#333] text-xs leading-none px-1">✕</button>
            )}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {loadingScenarios ? (
              <div className="py-8 text-center text-xs text-sap-textLight">Loading Scenarios…</div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-xs text-sap-textLight italic">
                {scenarios.length === 0 ? 'No existing scenarios found.' : 'No scenarios match the search.'}
              </div>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#e8e8e8] sticky top-0">
                  <tr>
                    <th className="w-8 px-2 py-1.5 text-center text-[#333] font-semibold border border-[#c0c0c0]">#</th>
                    <th className="px-2 py-1.5 text-left text-[#333] font-semibold border border-[#c0c0c0]">Scenario Name</th>
                    <th className="px-2 py-1.5 text-left text-[#333] font-semibold border border-[#c0c0c0]">Description</th>
                    <th className="px-2 py-1.5 text-center text-[#333] font-semibold border border-[#c0c0c0]">Start Date</th>
                    <th className="px-2 py-1.5 text-center text-[#333] font-semibold border border-[#c0c0c0]">End Date</th>
                    <th className="px-2 py-1.5 text-center text-[#333] font-semibold border border-[#c0c0c0]">Updated On</th>
                    <th className="px-2 py-1.5 text-left text-[#333] font-semibold border border-[#c0c0c0]">Updated By</th>
                    <th className="px-2 py-1.5 text-center text-[#333] font-semibold border border-[#c0c0c0]">Last Executed On</th>
                    <th className="px-2 py-1.5 text-center text-[#333] font-semibold border border-[#c0c0c0]" title="How many times this sales order has been planned">Runs</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((sc, idx) => (
                    <tr key={sc.run_id ?? `so-${sc.so_id}`}
                      onClick={() => selectScenario(sc)}
                      className={`cursor-pointer transition-colors
                        ${(selectedScenario?.run_id != null && selectedScenario?.run_id === sc.run_id) ||
                          (selectedScenario?.so_id != null && selectedScenario?.so_id === sc.so_id)
                          ? 'bg-[#b8d4f0]'
                          : sc.row_type === 'so'
                            ? 'bg-[#f0faf0] hover:bg-[#d8f0d8]'
                            : 'bg-white hover:bg-[#dce9f5]'}`}>
                      <td className="px-2 py-1 text-center text-[#333] border border-[#c0c0c0]">{idx + 1}</td>
                      <td className={`px-2 py-1 border border-[#c0c0c0] ${(selectedScenario?.run_id === sc.run_id || selectedScenario?.so_id === sc.so_id) ? 'text-[#333] font-medium' : 'text-[#333]'}`}>
                        <div className="flex items-center gap-1.5">
                          {sc.row_type === 'so' && (
                            <span className="px-1 py-0.5 text-[9px] font-bold bg-green-100 text-green-700 rounded border border-green-300 leading-none">SO</span>
                          )}
                          <span>{sc.project_number && sc.project_number !== sc.run_name ? `${sc.project_number} ${sc.run_name}` : sc.run_name}</span>
                        </div>
                        {sc.so_numbers && sc.row_type !== 'so' && <div className="text-[10px] text-sap-blue mt-0.5">{sc.so_numbers}</div>}
                      </td>
                      <td className="px-2 py-1 text-[#333] border border-[#c0c0c0]">{sc.description || '—'}</td>
                      <td className="px-2 py-1 text-center text-[#333] border border-[#c0c0c0]">{fmtDate(sc.start_date)}</td>
                      <td className="px-2 py-1 text-center text-[#333] border border-[#c0c0c0]">{fmtDate(sc.end_date)}</td>
                      <td className="px-2 py-1 text-center text-[#333] border border-[#c0c0c0]">{fmtDate(sc.updated_at)}</td>
                      <td className="px-2 py-1 text-[#333] border border-[#c0c0c0]">{sc.created_by || '—'}</td>
                      <td className="px-2 py-1 text-center text-[#333] border border-[#c0c0c0]">{fmtDate(sc.created_at)}</td>
                      <td className="px-2 py-1 text-center text-[#333] border border-[#c0c0c0]">
                        {sc.row_type === 'so' ? '—' : (sc.run_count ?? 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selectedScenario && (
            <RerunNotice eligibility={eligibility} loading={eligibilityLoading} isPartialRun={isPartialRun} />
          )}
        </div>
      )}
    </div>
  );
}

function StepScenarioDetails({ form, setForm, details, setDetails, holidays, navigate, scenarioMode }) {
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const s  = (k, v) => setDetails(d => ({ ...d, [k]: v }));
  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <p className="page-title">Scenario Details</p>
        <p className="text-xs text-sap-textLight mt-0.5">Define the planning horizon that you want to display in the MRP report.</p>
      </div>

      {/* ── Description (read-only from step 1, only for create mode) ── */}
      {scenarioMode === 'create' && (
        <div className="flex items-center gap-3">
          <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">Description</label>
          <span className="flex-1 h-8 px-3 flex items-center text-xs text-sap-text bg-sap-bg border border-sap-border rounded-lg">{form.runName}</span>
        </div>
      )}

      {/* ── Planning Horizon ── */}
      <div className="border border-sap-border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-sap-bg border-b border-sap-border">
          <span className="text-xs font-semibold text-sap-blue">Planning Horizon</span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center gap-3">
            <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">Start Date *</label>
            <input type="date" value={form.startDate} onChange={e => sf('startDate', e.target.value)}
              className="flex-1 h-8 px-2 text-xs border border-sap-border rounded-lg bg-white
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
          </div>
          <div className="flex items-center gap-3">
            <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">End Date *</label>
            <input type="date" value={form.endDate} onChange={e => sf('endDate', e.target.value)}
              className="flex-1 h-8 px-2 text-xs border border-sap-border rounded-lg bg-white
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
          </div>
          <div className="flex items-center gap-3">
            <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">View Data in Periods Of</label>
            <input type="number" min="1" value={details.periodQty} onChange={e => s('periodQty', e.target.value)}
              className="w-20 h-8 px-2 text-xs border border-sap-border rounded-lg bg-white text-center
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
            <select value={details.periodUnit} onChange={e => s('periodUnit', e.target.value)}
              className="h-8 px-2 text-xs border border-sap-border rounded-lg bg-white
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
              <option>Days</option>
              <option>Weeks</option>
              <option>Months</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">Planning Horizon Length</label>
            <input type="number" min="1" value={details.horizonLength} onChange={e => s('horizonLength', e.target.value)}
              className="w-20 h-8 px-2 text-xs border border-sap-border rounded-lg bg-white text-center
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
            <span className="text-xs text-sap-textLight">Months</span>
          </div>
        </div>
      </div>

      {/* ── Consider Holidays For ── */}
      <div className="border border-sap-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-sap-bg border-b border-sap-border">
          <div className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span className="text-xs font-semibold text-sap-blue">Consider Holidays For</span>
          </div>
          <button onClick={() => navigate('/mrp/calendar')}
            className="text-2xs text-sap-blue hover:underline font-medium">
            Manage Holidays →
          </button>
        </div>
        <div className="px-4 py-3 space-y-2.5">
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.holidaysProduction} onChange={e => s('holidaysProduction', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Production Items
            </label>
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.holidaysPurchase} onChange={e => s('holidaysPurchase', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Purchase Items
            </label>
          </div>

          {/* Holiday summary */}
          {holidays ? (
            <div className="space-y-2">
              <div className="flex gap-3 text-xs">
                <div className="flex-1 bg-[#FFF8F0] rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-[#B8860B]">{holidays.holiday_count}</p>
                  <p className="text-2xs text-[#B8860B]/70">Holidays</p>
                </div>
                <div className="flex-1 bg-[#F0F4F3] rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-[#6B7F7F]">{holidays.weekend_count}</p>
                  <p className="text-2xs text-[#6B7F7F]">Weekends</p>
                </div>
                <div className="flex-1 bg-[#FFF0F0] rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-[#C0392B]">{holidays.total_non_working}</p>
                  <p className="text-2xs text-[#C0392B]/70">Non-Working</p>
                </div>
              </div>
              {holidays.holidays?.length > 0 && (
                <div className="max-h-28 overflow-y-auto border border-sap-border/50 rounded-lg">
                  <table className="w-full text-2xs">
                    <thead className="bg-sap-bg sticky top-0">
                      <tr>
                        <th className="text-left px-2.5 py-1 font-semibold text-sap-textLight">Date</th>
                        <th className="text-left px-2.5 py-1 font-semibold text-sap-textLight">Type</th>
                        <th className="text-left px-2.5 py-1 font-semibold text-sap-textLight">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holidays.holidays.map((h, i) => (
                        <tr key={i} className="border-t border-sap-border/30">
                          <td className="px-2.5 py-1 text-sap-text whitespace-nowrap">
                            {new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            {h.is_recurring ? <span className="ml-1 text-sap-textLight">↻</span> : ''}
                          </td>
                          <td className="px-2.5 py-1">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-2xs font-medium
                              ${h.leave_type === 'Public Holiday' ? 'bg-sap-blue/15 text-sap-blueDeeper' :
                                h.leave_type === 'Company Holiday' ? 'bg-[#7B6FA0]/15 text-[#7B6FA0]' :
                                'bg-[#B8860B]/15 text-[#B8860B]'}`}>
                              {h.leave_type}
                            </span>
                          </td>
                          <td className="px-2.5 py-1 text-sap-text">{h.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-2xs text-sap-textLight italic">
                Lead times automatically skip non-working days based on selected item types above.
              </p>
            </div>
          ) : (
            <p className="text-2xs text-sap-textLight italic">Set planning dates in Step 1 to preview holidays.</p>
          )}
        </div>
      </div>

      {/* ── Additional Options ── */}
      <div className="flex items-center gap-3">
        <span className="w-48 shrink-0" />
        <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
          <input type="checkbox" checked={details.ignoreCumulativeLeadTime} onChange={e => s('ignoreCumulativeLeadTime', e.target.checked)}
            className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
          Ignore Cumulative Lead Time
        </label>
      </div>

      {/* ── Display Preferences ── */}
      <div className="border border-sap-border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-sap-bg border-b border-sap-border">
          <span className="text-xs font-semibold text-sap-blue">Display Preferences</span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center gap-3">
            <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">Sort By</label>
            <select value={details.sortBy} onChange={e => s('sortBy', e.target.value)}
              className="flex-1 h-8 px-2 text-xs border border-sap-border rounded-lg bg-white
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
              <option>Assembly Sequence</option>
              <option>Item Number</option>
              <option>Item Description</option>
              <option>Item Group</option>
            </select>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 ml-[12.75rem]">
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.displayNoRequirements} onChange={e => s('displayNoRequirements', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Display Items with No Requirements
            </label>
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.displaySelectedOnly} onChange={e => s('displaySelectedOnly', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Display Selected Items Only
            </label>
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.simulation} onChange={e => s('simulation', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Simulation
            </label>
          </div>
        </div>
      </div>

      {/* ── Data Source Options ── */}
      <div className="border border-sap-border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-sap-bg border-b border-sap-border">
          <span className="text-xs font-semibold text-sap-blue">Data Sources</span>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center gap-3">
            <label className="w-48 text-xs font-medium text-sap-textLight text-right shrink-0">Safety Stock Factor</label>
            <input type="text" value={details.safety} onChange={e => s('safety', e.target.value)} placeholder="1.0"
              className="w-20 h-8 px-2 text-xs border border-sap-border rounded-lg bg-white text-center
                         focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 ml-[12.75rem]">
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.openPO} onChange={e => s('openPO', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Consider Open Purchase Orders
            </label>
            <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="checkbox" checked={details.forecasts} onChange={e => s('forecasts', e.target.checked)}
                className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
              Consider Forecast Demand
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepScope({ scope, setScope }) {
  const [allItems, setAllItems]     = useState([]);
  const [mrpItems, setMrpItems]     = useState([]);   // all MRP items for "All Items" view
  const [search, setSearch]         = useState('');
  const [tableSearch, setTableSearch] = useState('');  // search within main table
  const [loadingItems, setLoading]  = useState(false);
  const [loadingMrp, setLoadingMrp] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSel, setPickerSel]   = useState([]);   // item_ids checked in picker
  const [tableSel, setTableSel]     = useState([]);   // item_ids checked in main table
  const [projectCodeInput, setProjectCodeInput] = useState('');
  const [projectLoading, setProjectLoading] = useState(false);
  // SO lines (BOMs) ticked for a partial run; filled when a sales order is loaded.
  // Belongs here, with the panel that uses it — as wizard state it was out of scope
  // in this component and every render of the panel threw, blanking the page.
  const [selectedSoLines, setSelectedSoLines] = useState([]);
  const [projectMsg, setProjectMsg] = useState('');
  const [projectInfo, setProjectInfo] = useState(null); // {items, sos, newCount, existingCount, lastMrpRunDate}
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [selectedSoId, setSelectedSoId] = useState('');
  const [projectComboOpen, setProjectComboOpen] = useState(false);
  const [projectListFilter, setProjectListFilter] = useState('');
  const projectComboRef = useRef(null);
  const [listPage, setListPage] = useState(1);

  const pickItemMode = scope.mode === 'Selected Items' || scope.mode === SCOPE_BY_SALES_PROJECT;

  const filteredProjectOptions = useMemo(() => {
    const q = projectListFilter.trim().toLowerCase();
    if (!q) return projectOptions;
    return projectOptions.filter((o) => {
      const parts = [
        o.match_code,
        o.so_number,
        o.bp_project,
        o.customer_name,
      ].map((x) => (x != null ? String(x).toLowerCase() : ''));
      return parts.some((p) => p.includes(q));
    });
  }, [projectOptions, projectListFilter]);

  useEffect(() => {
    if (!projectComboOpen) return;
    const onDoc = (e) => {
      if (projectComboRef.current && !projectComboRef.current.contains(e.target)) {
        setProjectComboOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [projectComboOpen]);

  /* Load all MRP items on mount for the "All Items" view */
  useEffect(() => {
    setLoadingMrp(true);
    api.get('/mrp/items-list')
      .then(d => setMrpItems(Array.isArray(d) ? d : []))
      .catch(() => setMrpItems([]))
      .finally(() => setLoadingMrp(false));
  }, []);

  /* Load all items for the picker */
  useEffect(() => {
    if (!showPicker) return;
    setLoading(true);
    api.get('/mrp/items-list')
      .then(d => setAllItems(Array.isArray(d) ? d : []))
      .catch(() => setAllItems([]))
      .finally(() => setLoading(false));
  }, [showPicker]);

  /* When leaving project mode, reset picker state */
  useEffect(() => {
    if (scope.mode !== SCOPE_BY_SALES_PROJECT) {
      setProjectOptions([]);
      setSelectedSoId('');
      setProjectComboOpen(false);
      setProjectListFilter('');
    }
  }, [scope.mode]);

  /* Load sales orders for project dropdown when this mode is active or when searching */
  useEffect(() => {
    if (scope.mode !== SCOPE_BY_SALES_PROJECT) return;
    const q = projectListFilter.trim();
    const timer = setTimeout(() => {
      setProjectOptionsLoading(true);
      const url = q
        ? `/mrp/sales-order-project-options?q=${encodeURIComponent(q)}&limit=100`
        : '/mrp/sales-order-project-options?limit=150';
      api
        .get(url)
        .then((rows) => {
          const list = Array.isArray(rows) ? rows : [];
          setProjectOptions(list);
          setSelectedSoId((prev) => {
            if (!prev) return '';
            return list.some((o) => String(o.so_id) === String(prev)) ? prev : '';
          });
        })
        .catch(() => setProjectOptions([]))
        .finally(() => setProjectOptionsLoading(false));
    }, q ? 200 : 0);
    return () => clearTimeout(timer);
  }, [scope.mode, projectListFilter]);

  // Items shown in the "All Items" view, filtered by search (memoized stable reference when inputs unchanged)
  const displayedMrpItems = useMemo(() => {
    if (!tableSearch.trim()) return mrpItems;
    const q = tableSearch.toLowerCase();
    return mrpItems.filter(
      (i) =>
        i.item_no.toLowerCase().includes(q) || i.description.toLowerCase().includes(q)
    );
  }, [mrpItems, tableSearch]);

  /** Single list driving the main table: all MRP items or selected/project lines */
  const displayedList = useMemo(() => {
    if (scope.mode === 'All Items') return displayedMrpItems;
    if (scope.mode === 'Selected Items' || scope.mode === SCOPE_BY_SALES_PROJECT) return scope.selectedItems;
    return [];
  }, [scope.mode, displayedMrpItems, scope.selectedItems]);

  const listTotal = displayedList.length;
  const listTotalPages = Math.max(1, Math.ceil(listTotal / STEP_SCOPE_PAGE_SIZE) || 1);
  const listPageSafe = Math.min(Math.max(1, listPage), listTotalPages);
  const listPageStart = (listPageSafe - 1) * STEP_SCOPE_PAGE_SIZE;
  const pagedListItems = displayedList.slice(listPageStart, listPageStart + STEP_SCOPE_PAGE_SIZE);
  const listRangeFrom = listTotal === 0 ? 0 : listPageStart + 1;
  const listRangeTo = listTotal === 0 ? 0 : Math.min(listPageStart + STEP_SCOPE_PAGE_SIZE, listTotal);

  const listPagingResetKeyRef = useRef('');
  useEffect(() => {
    const tp = Math.max(1, Math.ceil(displayedList.length / STEP_SCOPE_PAGE_SIZE));
    const resetKey = `${scope.mode}|${tableSearch}`;
    if (listPagingResetKeyRef.current !== resetKey) {
      listPagingResetKeyRef.current = resetKey;
      setListPage(1);
      return;
    }
    setListPage((prev) => (prev > tp ? tp : prev));
  }, [displayedList.length, tableSearch, scope.mode]);

  const filtered = allItems.filter(i =>
    !scope.selectedItems.find(s => s.item_id === i.item_id) &&
    (i.item_no.toLowerCase().includes(search.toLowerCase()) ||
     i.description.toLowerCase().includes(search.toLowerCase()))
  );

  const addSelected = () => {
    const toAdd = allItems.filter(i => pickerSel.includes(i.item_id));
    setScope(sc => ({
      ...sc,
      selectedItems: [...sc.selectedItems, ...toAdd],
      ...(sc.mode === SCOPE_BY_SALES_PROJECT ? { projectSalesOrderIds: [] } : {}),
    }));
    setPickerSel([]);
    setShowPicker(false);
    setSearch('');
  };

  const removeSelected = () => {
    setScope(sc => ({
      ...sc,
      selectedItems: sc.selectedItems.filter(i => !tableSel.includes(i.item_id)),
      ...(sc.mode === SCOPE_BY_SALES_PROJECT ? { projectSalesOrderIds: [] } : {}),
    }));
    setTableSel([]);
  };

  const togglePickerRow = id =>
    setPickerSel(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const toggleTableRow = id =>
    setTableSel(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const selectDeselectAll = () =>
    setTableSel(tableSel.length === scope.selectedItems.length ? [] : scope.selectedItems.map(i => i.item_id));

  /**
   * SO lines ticked for this run. A sales order often carries several machines, and
   * the client plans one at a time — "MRP should run for partial BOM as per sales
   * order". Untick a line and its BOM stays out of the import.
   */
  const toggleSoLine = (lineId) => setSelectedSoLines((prev) => (
    prev.includes(lineId) ? prev.filter((x) => x !== lineId) : [...prev, lineId]
  ));

  /** Item ids belonging to the ticked lines: the line item itself plus its components. */
  const itemIdsForSelectedLines = (info, lineIds, useExpanded) => {
    const ids = new Set();
    for (const ln of info.soLines || []) {
      if (!lineIds.includes(ln.line_id)) continue;
      if (ln.item_id) ids.add(Number(ln.item_id));
      if (useExpanded && ln.bom && !ln.bom.expand_skipped) {
        for (const c of ln.bom.components || []) if (c.item_id) ids.add(Number(c.item_id));
      }
    }
    return ids;
  };

  /**
   * Ticking a line reshapes the item list below straight away.
   *
   * The list is what the run actually plans, and leaving it showing all 18 items
   * while the panel said 15 made the tick look ignored. Only the ticked lines' items
   * stay; the Import buttons still narrow that to new/modified when asked.
   */
  useEffect(() => {
    if (!projectInfo || !(projectInfo.soLines || []).length) return;
    const useExpanded = projectInfo.expandMode !== 'so_lines';
    const pool = useExpanded ? projectInfo.items : (projectInfo.itemsOnOrder || projectInfo.items);
    const partial = selectedSoLines.length < projectInfo.soLines.length;
    const allowed = partial ? itemIdsForSelectedLines(projectInfo, selectedSoLines, useExpanded) : null;
    const chosen = allowed ? pool.filter((i) => allowed.has(Number(i.item_id))) : pool;
    const lineItemIds = partial
      ? (projectInfo.soLines || []).filter((l) => selectedSoLines.includes(l.line_id))
        .map((l) => Number(l.item_id)).filter(Boolean)
      : null;
    setScope((sc) => (
      sc.selectedItems.length === chosen.length
        && sc.selectedItems.every((i, n) => i.item_id === chosen[n].item_id)
        && String(sc.selectedSoLineItemIds) === String(lineItemIds)
        ? sc
        : {
          ...sc,
          mode: SCOPE_BY_SALES_PROJECT,
          selectedItems: chosen,
          projectSalesOrderIds: projectInfo.soIds,
          selectedSoLineItemIds: lineItemIds,
        }
    ));
    setListPage(1);
  }, [selectedSoLines, projectInfo]);

  const loadItemsFromProject = async () => {
    const code = projectCodeInput.trim();
    if (!code) {
      setProjectMsg('Enter a project code or sales order number.');
      return;
    }
    setProjectLoading(true);
    setProjectMsg('');
    setProjectInfo(null);
    try {
      const data = await api.get(`/mrp/items-by-project-code?code=${encodeURIComponent(code)}`);
      const items = Array.isArray(data?.items) ? data.items : [];
      const sos = Array.isArray(data?.sales_orders) ? data.sales_orders : [];
      const ids = sos.map((o) => o.so_id).filter((id) => id != null);
      if (!items.length) {
        setScope((sc) => ({
          ...sc,
          mode: SCOPE_BY_SALES_PROJECT,
          selectedItems: [],
          projectSalesOrderIds: [],
        }));
        setListPage(1);
        setProjectMsg(data?.message || 'No line items found for matching orders.');
        return;
      }
      const itemsOnOrder = Array.isArray(data?.items_on_order) ? data.items_on_order : items;
      const newItems = items.filter((i) => !i.already_in_mrp);
      const existingItems = items.filter((i) => i.already_in_mrp);

      const infoObj = {
        items,
        itemsOnOrder,
        sos,
        soIds: ids,
        newCount: data.new_items_count ?? newItems.length,
        existingCount: data.existing_items_count ?? existingItems.length,
        newCountOnOrder: data.new_items_count_on_order ?? itemsOnOrder.filter((i) => !i.already_in_mrp).length,
        existingCountOnOrder: data.existing_items_count_on_order ?? itemsOnOrder.filter((i) => i.already_in_mrp).length,
        lastMrpRunDate: data.last_mrp_run_date || null,
        soSummary: data.so_summary || null,
        soLines: Array.isArray(data.so_lines) ? data.so_lines : [],
        projectChanges: Array.isArray(data.project_changes) ? data.project_changes : [],
        soModifiedSinceMrp: Boolean(data.so_modified_since_mrp),
        expandMode: 'bom_expanded',
      };
      setProjectInfo(infoObj);
      setSelectedSoLines(infoObj.soLines.map((l) => l.line_id));

      // Load items into main table
      setScope((sc) => ({
        ...sc,
        mode: SCOPE_BY_SALES_PROJECT,
        selectedItems: items,
        projectSalesOrderIds: ids,
      }));
      setListPage(1);
      const soLabel = sos.map((o) => o.so_number || o.bp_project).filter(Boolean).join(', ');
      setProjectMsg(
        `Loaded ${items.length} item${items.length !== 1 ? 's' : ''} (${itemsOnOrder.length} BOM headers / SO lines) from ${sos.length} sales order${sos.length !== 1 ? 's' : ''}${soLabel ? `: ${soLabel}` : ''}.`
      );
    } catch (e) {
      setProjectMsg(e.message || 'Could not load items for this project code.');
    } finally {
      setProjectLoading(false);
    }
  };

  const importProjectItems = (mode, expandMode) => {
    if (!projectInfo) return;
    const useExpanded = (expandMode || projectInfo.expandMode) !== 'so_lines';
    const wholePool = useExpanded ? projectInfo.items : (projectInfo.itemsOnOrder || projectInfo.items);
    // A partial run: only the ticked SO lines and what is inside their BOMs.
    const partial = (projectInfo.soLines || []).length > 0
      && selectedSoLines.length < (projectInfo.soLines || []).length;
    const allowed = partial ? itemIdsForSelectedLines(projectInfo, selectedSoLines, useExpanded) : null;
    const pool = allowed ? wholePool.filter((i) => allowed.has(Number(i.item_id))) : wholePool;
    const chosen = mode === 'new'
      ? pool.filter((i) => !i.already_in_mrp)
      : pool;
    setScope((sc) => ({
      ...sc,
      mode: SCOPE_BY_SALES_PROJECT,
      selectedItems: chosen,
      projectSalesOrderIds: projectInfo.soIds,
      selectedSoLineItemIds: partial
        ? (projectInfo.soLines || []).filter((l) => selectedSoLines.includes(l.line_id))
          .map((l) => Number(l.item_id)).filter(Boolean)
        : null,
    }));
    setListPage(1);
    const soLabel = projectInfo.sos.map((o) => o.so_number || o.bp_project).filter(Boolean).join(', ');
    const scopeLabel = useExpanded ? 'BOM expanded' : 'BOM Headers / SO lines only';
    const label = mode === 'new' ? 'new/modified' : 'all';
    const partLabel = partial
      ? ` — partial run: ${selectedSoLines.length} of ${projectInfo.soLines.length} SO lines`
      : '';
    setProjectMsg(
      `Loaded ${chosen.length} ${label} item${chosen.length !== 1 ? 's' : ''} (${scopeLabel})${partLabel} from ${projectInfo.sos.length} sales order${projectInfo.sos.length !== 1 ? 's' : ''}${soLabel ? `: ${soLabel}` : ''}.`
    );
  };

  return (
    <div>
      <div className="mb-4">
        <p className="page-title">Item Selection</p>
        <p className="text-xs text-sap-textLight mt-0.5">
          To plan material requirements for all items with the planning method "MRP", select "All Items".
          To plan for specific items, use "Selected Items" and "Add Items", or choose "By project code (sales order)"
          to load every item from sales order lines for that BP project code or order number.
        </p>
      </div>

      {/* Radio buttons */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
        {['All Items', 'Selected Items', SCOPE_BY_SALES_PROJECT].map(opt => (
          <label key={opt} className="flex items-center gap-2 text-xs text-sap-text cursor-pointer font-medium">
            <input type="radio" name="scopeMode" value={opt}
              checked={scope.mode === opt}
              onChange={() => setScope(sc => ({
                ...sc,
                mode: opt,
                ...(opt !== SCOPE_BY_SALES_PROJECT ? { projectSalesOrderIds: [] } : {}),
              }))}
              className="accent-sap-blue w-3.5 h-3.5 shrink-0" />
            <span className="leading-tight">{opt}</span>
          </label>
        ))}
        {scope.mode === 'All Items' && (
          <span className="text-2xs text-sap-textLight ml-2">
            {mrpItems.length} MRP item{mrpItems.length !== 1 ? 's' : ''}
            {tableSearch && ` · ${displayedMrpItems.length} matching`}
          </span>
        )}
      </div>

      {scope.mode === SCOPE_BY_SALES_PROJECT && (
        <div className="mb-4 p-3 rounded-lg border border-sap-border bg-sap-bg/80 space-y-3">
          <p className="text-2xs text-sap-textLight">
            Matches <span className="font-semibold text-sap-text">BP Project</span> or{' '}
            <span className="font-semibold text-sap-text">Sales order number</span> (not cancelled). All active item lines on those orders are added to the list below.
          </p>
          <div className="grid gap-3 sm:grid-cols-1 max-w-3xl">
            <div>
              <label className="block text-2xs font-semibold text-sap-text mb-1">
                Select sales order / project
              </label>
              <p className="text-2xs text-sap-textLight mb-1.5">
                Type a BP project code or order number directly, or open the list to search up to {projectOptions.length || 150} recent orders.
              </p>
              <div className="flex flex-wrap items-start gap-2">
                <div ref={projectComboRef} className="relative flex-1 min-w-[220px]">
                  <div className="flex rounded-lg border border-sap-border bg-white focus-within:ring-2 focus-within:ring-sap-blue/30 focus-within:border-sap-blue">
                    <input
                      type="text"
                      value={projectCodeInput}
                      disabled={projectOptionsLoading}
                      onChange={(e) => {
                        setProjectCodeInput(e.target.value);
                        setProjectMsg('');
                        setProjectInfo(null);
                        setSelectedSoId('');
                      }}
                      onFocus={() => {
                        setProjectListFilter('');
                        setProjectComboOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setProjectComboOpen(false);
                          return;
                        }
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          setProjectComboOpen(false);
                          loadItemsFromProject();
                        }
                      }}
                      placeholder={projectOptionsLoading ? 'Loading orders…' : 'Type code or click list…'}
                      className="flex-1 min-w-0 h-9 pl-3 pr-2 text-xs border-0 rounded-l-lg bg-transparent
                                 focus:outline-none disabled:opacity-60"
                    />
                    <button
                      type="button"
                      disabled={projectOptionsLoading}
                      aria-expanded={projectComboOpen}
                      aria-label="Open order list"
                      onClick={() => {
                        setProjectComboOpen((o) => !o);
                        if (!projectComboOpen) setProjectListFilter('');
                      }}
                      className="px-2.5 h-9 text-sap-textLight hover:text-sap-text border-l border-sap-border shrink-0
                                 disabled:opacity-50 rounded-r-lg">
                      <span className="text-[10px] leading-none">{projectComboOpen ? '▲' : '▼'}</span>
                    </button>
                  </div>
                  {projectComboOpen && !projectOptionsLoading && (
                    <div
                      className="absolute z-50 left-0 right-0 top-full mt-0.5 rounded-lg border border-sap-border bg-white shadow-lg overflow-hidden flex flex-col max-h-72">
                      <div className="p-2 border-b border-sap-border bg-sap-bg/80">
                        <input
                          type="search"
                          autoFocus
                          value={projectListFilter}
                          onChange={(e) => setProjectListFilter(e.target.value)}
                          placeholder="Search order #, project, customer…"
                          className="w-full h-8 px-2.5 text-xs border border-sap-border rounded-md bg-white
                                     focus:outline-none focus:ring-2 focus:ring-sap-blue/30"
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') e.preventDefault();
                          }}
                        />
                      </div>
                      <ul className="overflow-y-auto text-xs max-h-52">
                        {filteredProjectOptions.length === 0 ? (
                          <li className="px-3 py-3 text-sap-textLight italic text-center">No matching orders.</li>
                        ) : (
                          filteredProjectOptions.map((o) => {
                            const code = String(o.match_code || o.so_number || '').trim() || '—';
                            const cust = String(o.customer_name || '').trim();
                            return (
                              <li key={o.so_id}>
                                <button
                                  type="button"
                                  className={`w-full text-left px-3 py-2 hover:bg-sap-blueLight border-b border-sap-border/40 last:border-0
                                    ${String(selectedSoId) === String(o.so_id) ? 'bg-sap-blue/10' : ''}`}
                                  onClick={() => {
                                    setSelectedSoId(String(o.so_id));
                                    setProjectCodeInput(code);
                                    setProjectMsg('');
                                    setProjectInfo(null);
                                    setProjectComboOpen(false);
                                    setProjectListFilter('');
                                  }}>
                                  <span className="font-semibold text-sap-blue">{code}</span>
                                  <span className="text-sap-text"> · SO {o.so_number || '—'}</span>
                                  {cust ? (
                                    <span className="block text-sap-textLight truncate" title={cust}>{cust}</span>
                                  ) : null}
                                </button>
                              </li>
                            );
                          })
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={loadItemsFromProject}
                  disabled={projectLoading || projectOptionsLoading}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-sap-blue text-white hover:bg-sap-blueDark disabled:opacity-50 shrink-0 h-9">
                  {projectLoading ? 'Loading…' : 'Load items'}
                </button>
              </div>
            </div>
          </div>
          {projectMsg && (
            <p className={`text-2xs ${scope.selectedItems.length ? 'text-sap-text' : 'text-amber-800'}`}>{projectMsg}</p>
          )}

          {/* Import choice panel — SO/BOM breakdown and import options */}
          {projectInfo && (() => {
            const fmtDt = (v) => {
              if (!v) return '—';
              const d = new Date(v);
              return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
                ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
            };
            const useExpanded = projectInfo.expandMode !== 'so_lines';
            const linePool = useExpanded ? projectInfo.items : (projectInfo.itemsOnOrder || projectInfo.items);
            // Counts follow the ticked SO lines, so "Import all (18)" cannot promise
            // more than a partial run actually imports.
            const partialRun = (projectInfo.soLines || []).length > 0
              && selectedSoLines.length < projectInfo.soLines.length;
            const allowedIds = partialRun
              ? itemIdsForSelectedLines(projectInfo, selectedSoLines, useExpanded) : null;
            const shownPool = allowedIds ? linePool.filter((i) => allowedIds.has(Number(i.item_id))) : linePool;
            const totalShown = allowedIds
              ? shownPool.length
              : (useExpanded ? projectInfo.newCount + projectInfo.existingCount
                : projectInfo.newCountOnOrder + projectInfo.existingCountOnOrder);
            const newShown = allowedIds
              ? shownPool.filter((i) => !i.already_in_mrp).length
              : (useExpanded ? projectInfo.newCount : projectInfo.newCountOnOrder);
            const existingShown = allowedIds
              ? shownPool.filter((i) => i.already_in_mrp).length
              : (useExpanded ? projectInfo.existingCount : projectInfo.existingCountOnOrder);
            const summary = projectInfo.soSummary;
            return (
            <div className="mt-2 p-3 rounded-lg border border-blue-200 bg-blue-50/60 space-y-3">
              {projectInfo.sos.map((so) => (
                  <div key={so.so_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold text-sap-text">
                        Sales Order {so.so_number}
                        {so.customer_name ? <span className="font-normal text-sap-textLight"> — {so.customer_name}</span> : null}
                      </p>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-2xs text-sap-textLight">
                        <span>Created: <strong className="text-sap-text">{fmtDt(so.created_at)}</strong></span>
                        {so.updated_at && so.created_at && new Date(so.updated_at).getTime() > new Date(so.created_at).getTime() && (
                          <span>Modified: <strong className="text-sap-text">{fmtDt(so.updated_at)}</strong></span>
                        )}
                        {projectInfo.lastMrpRunDate && (
                          <span>Last MRP Run: <strong className="text-sap-text">{fmtDt(projectInfo.lastMrpRunDate)}</strong></span>
                        )}
                      </div>
                    </div>
                  </div>
              ))}

              {summary && (
                <p className="text-2xs text-sap-text">
                  <strong>{summary.total_lines}</strong> line{summary.total_lines !== 1 ? 's' : ''} on this sales order:{' '}
                  <strong>{summary.plain_item_count}</strong> plain item{summary.plain_item_count !== 1 ? 's' : ''}
                  {summary.bom_parent_count > 0 && (
                    <> and <strong className="text-sap-blueDeeper">{summary.bom_parent_count}</strong> BOM parent{summary.bom_parent_count !== 1 ? 's' : ''}</>
                  )}.
                </p>
              )}

              {projectInfo.soLines.length > 0 && (
                <div className="rounded border border-sap-border/60 bg-white/80 overflow-hidden">
                  <table className="w-full text-2xs">
                    <thead className="bg-sap-bg/80 text-sap-textLight">
                      <tr>
                        <th className="text-center px-2 py-1.5 font-semibold w-10" title="Tick the BOMs to plan in this run">
                          <input
                            type="checkbox"
                            className="accent-sap-blue"
                            checked={selectedSoLines.length === projectInfo.soLines.length}
                            onChange={() => setSelectedSoLines(
                              selectedSoLines.length === projectInfo.soLines.length
                                ? [] : projectInfo.soLines.map((l) => l.line_id)
                            )}
                          />
                        </th>
                        <th className="text-left px-2 py-1.5 font-semibold">SO line</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Type</th>
                        <th className="text-left px-2 py-1.5 font-semibold">BOM / components</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectInfo.soLines.map((ln) => (
                        <tr key={ln.line_id}
                          className={`border-t border-sap-border/40 ${selectedSoLines.includes(ln.line_id) ? '' : 'opacity-50'}`}>
                          <td className="px-2 py-1.5 text-center">
                            <input type="checkbox" className="accent-sap-blue"
                              checked={selectedSoLines.includes(ln.line_id)}
                              onChange={() => toggleSoLine(ln.line_id)} />
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="font-mono font-semibold text-sap-blue">{ln.item_no}</span>
                            {ln.description ? <span className="block text-sap-textLight truncate max-w-[200px]">{ln.description}</span> : null}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            {ln.line_kind === 'bom_parent' ? (
                              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-semibold">BOM</span>
                            ) : (
                              <span className="text-sap-textLight">Plain item</span>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            {ln.bom ? (
                              <div className="space-y-0.5">
                                {ln.bom.changed_since_last_mrp && (
                                  <span className="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-900 font-semibold mr-1">
                                    {ln.bom.change_reason === 'added_to_sales_order' ? 'Added to SO since last MRP' : 'BOM updated since last MRP'}
                                  </span>
                                )}
                                {ln.bom.expand_skipped ? (
                                  <span className="text-amber-800">Large BOM ({ln.bom.component_count} lines) — will import parent item</span>
                                ) : (
                                  <span className="text-sap-text">
                                    {ln.bom.component_count} component{ln.bom.component_count !== 1 ? 's' : ''}:{' '}
                                    {(ln.bom.components || []).map((c) => c.item_no).filter(Boolean).join(', ')}
                                  </span>
                                )}
                                {ln.bom.created_at && (
                                  <span className="block text-sap-textLight">BOM created: {fmtDt(ln.bom.created_at)}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sap-textLight">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {projectInfo.projectChanges.length > 0 && (
                <div>
                  <p className="text-2xs font-semibold text-sap-text mb-1">Recent SO line changes</p>
                  <ul className="text-2xs text-sap-textLight space-y-0.5 max-h-24 overflow-y-auto">
                    {projectInfo.projectChanges.slice(0, 8).map((c, i) => (
                      <li key={`${c.item_no}-${c.changed_at}-${i}`}>
                        <span className={c.change_type === 'added' ? 'text-green-800 font-medium' : 'text-red-800 font-medium'}>
                          {c.change_type === 'added' ? '+' : '−'}
                        </span>{' '}
                        {c.item_no} {c.item_description ? `— ${c.item_description}` : ''}
                        {c.changed_at ? ` (${fmtDt(c.changed_at)})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <p className="text-2xs font-semibold text-sap-text mb-1.5">How should BOM lines be imported?</p>
                <div className="flex flex-col gap-1.5 text-2xs">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mrpExpandMode"
                      checked={projectInfo.expandMode === 'bom_expanded'}
                      onChange={() => {
                        setProjectInfo((p) => ({ ...p, expandMode: 'bom_expanded' }));
                        setScope((sc) => ({ ...sc, selectedItems: projectInfo.items }));
                        setListPage(1);
                      }}
                      className="accent-sap-blue mt-0.5"
                    />
                    <span>
                      <strong>Expand BOMs to components</strong> — import materials inside each BOM (recommended for MRP).
                      <span className="block text-sap-textLight">
                        {projectInfo.newCount + projectInfo.existingCount} item{projectInfo.newCount + projectInfo.existingCount !== 1 ? 's' : ''} after expansion
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mrpExpandMode"
                      checked={projectInfo.expandMode === 'so_lines'}
                      onChange={() => {
                        setProjectInfo((p) => ({ ...p, expandMode: 'so_lines' }));
                        setScope((sc) => ({ ...sc, selectedItems: projectInfo.itemsOnOrder || projectInfo.items }));
                        setListPage(1);
                      }}
                      className="accent-sap-blue mt-0.5"
                    />
                    <span>
                      <strong>Keep SO lines as on order (BOM Headers Only)</strong> — import the {summary?.total_lines ?? '…'} item numbers exactly as on the sales order.
                      <span className="block text-sap-textLight">
                        {projectInfo.newCountOnOrder + projectInfo.existingCountOnOrder} item{(projectInfo.newCountOnOrder + projectInfo.existingCountOnOrder) !== 1 ? 's' : ''} on order
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-1 text-2xs text-sap-text">
                <span>Total to import: <strong>{totalShown}</strong></span>
                <span>Already in MRP: <strong>{existingShown}</strong></span>
                <span>New/Modified: <strong className="text-sap-blueDeeper">{newShown}</strong></span>
              </div>

              {projectInfo.soLines.length > 1 && (
                <p className="text-2xs text-sap-text">
                  Planning <strong>{selectedSoLines.length}</strong> of{' '}
                  <strong>{projectInfo.soLines.length}</strong> SO line{projectInfo.soLines.length !== 1 ? 's' : ''}
                  {partialRun ? ' — partial run' : ''}. Untick a line to leave its BOM out of this run.
                </p>
              )}
              <p className="text-2xs text-sap-textLight">
                {projectInfo.soModifiedSinceMrp
                  ? 'This sales order has been modified since the last MRP run. Choose which items to import:'
                  : 'Choose which items to import into this MRP run:'}
              </p>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={selectedSoLines.length === 0}
                  onClick={() => importProjectItems('new', projectInfo.expandMode)}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-sap-blue text-white hover:bg-sap-blueDark transition-colors disabled:opacity-40">
                  Import only new/modified ({newShown})
                </button>
                <button type="button" disabled={selectedSoLines.length === 0}
                  onClick={() => importProjectItems('all', projectInfo.expandMode)}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-sap-border bg-white text-sap-text hover:bg-sap-bg transition-colors disabled:opacity-40">
                  Import all ({totalShown})
                </button>
                {selectedSoLines.length === 0 && (
                  <span className="self-center text-2xs text-amber-700">Tick at least one SO line to import.</span>
                )}
              </div>
            </div>
            );
          })()}
        </div>
      )}

      {/* Search within table */}
      {scope.mode === 'All Items' && (
        <div className="mb-3">
          <input value={tableSearch} onChange={e => setTableSearch(e.target.value)}
            placeholder="Search items by no. or description…"
            className="w-72 h-7 px-3 text-xs border border-sap-border rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
        </div>
      )}

      {/* Table + side buttons layout */}
      <div className="flex gap-3">
        {/* Main table */}
        <div className="flex-1 border border-sap-border rounded-lg overflow-hidden flex flex-col min-h-0">
          <div
            className={`overflow-y-auto flex-1 min-h-0 ${
              scope.mode === 'All Items' || (pickItemMode && scope.selectedItems.length > 0)
                ? 'max-h-[min(420px,50vh)]'
                : ''
            }`}>
          <table className="w-full text-xs">
            <thead className="bg-sap-bg border-b border-sap-border sticky top-0">
              <tr>
                <th className="w-8 px-2 py-2 text-center text-sap-textLight font-semibold">#</th>
                <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Item No.</th>
                <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Item Description</th>
                {scope.mode === SCOPE_BY_SALES_PROJECT && (
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-36">Type</th>
                )}
              </tr>
            </thead>
            <tbody>
              {scope.mode === 'All Items' ? (
                loadingMrp ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-sap-textLight text-xs italic">
                      Loading MRP items…
                    </td>
                  </tr>
                ) : displayedList.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-sap-textLight text-xs italic">
                      {tableSearch ? 'No items match the search.' : 'No MRP items found.'}
                    </td>
                  </tr>
                ) : (
                  pagedListItems.map((item, idx) => (
                  <tr key={item.item_id}
                    className={`border-t border-sap-border/40 ${(listPageStart + idx) % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'}`}>
                    <td className="px-2 py-1.5 text-center text-sap-textLight">{listPageStart + idx + 1}</td>
                    <td className="px-3 py-1.5 text-sap-blue font-medium">{item.item_no}</td>
                    <td className="px-3 py-1.5 text-sap-text">{item.description}</td>
                  </tr>
                  ))
                )
              ) : scope.selectedItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-sap-textLight text-xs italic">
                    {scope.mode === SCOPE_BY_SALES_PROJECT
                      ? 'Enter a project code or sales order number, then click "Load items".'
                      : 'No items selected. Click "Add Items" to add specific items.'}
                  </td>
                </tr>
              ) : (
                pagedListItems.map((item, idx) => (
                  <tr key={item.item_id}
                    onClick={() => pickItemMode && toggleTableRow(item.item_id)}
                    className={`border-t border-sap-border/40 cursor-pointer transition-colors
                      ${tableSel.includes(item.item_id) ? 'bg-sap-blueLight' : 'hover:bg-sap-bg'}`}>
                    <td className="px-2 py-2 text-center text-sap-textLight">{listPageStart + idx + 1}</td>
                    <td className="px-3 py-2 text-sap-blue font-medium">{item.item_no}</td>
                    <td className="px-3 py-2 text-sap-text">{item.description}</td>
                    {scope.mode === SCOPE_BY_SALES_PROJECT && (
                      <td className="px-3 py-2 text-left whitespace-nowrap">
                        {item.is_bom_header || item.is_so_line ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                            ⭐ BOM Header
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-medium bg-gray-100 text-gray-700">
                            Component
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          {listTotal > 0 && (scope.mode === 'All Items' || pickItemMode) && (
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-sap-border bg-sap-bg shrink-0 text-2xs text-sap-textLight">
              <span>
                Showing{' '}
                <strong className="text-sap-text tabular-nums">{listRangeFrom}</strong>
                {'–'}
                <strong className="text-sap-text tabular-nums">{listRangeTo}</strong>
                {' of '}
                <strong className="text-sap-text tabular-nums">{listTotal.toLocaleString('en-IN')}</strong>
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={listPageSafe <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  className="px-2 py-1 rounded border border-sap-border bg-white text-sap-text hover:bg-sap-blueLight disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                >
                  Prev
                </button>
                <span className="tabular-nums px-1">
                  Page {listPageSafe} / {listTotalPages}
                </span>
                <button
                  type="button"
                  disabled={listPageSafe >= listTotalPages}
                  onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
                  className="px-2 py-1 rounded border border-sap-border bg-white text-sap-text hover:bg-sap-blueLight disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Side action buttons */}
        <div className="flex flex-col gap-2 w-36 shrink-0">
          {['Add Items', 'Remove Items', 'Select/Deselect All', 'Update Selected Items'].map(btn => (
            <button key={btn}
              disabled={!pickItemMode || (btn === 'Remove Items' && tableSel.length === 0)}
              onClick={() => {
                if (btn === 'Add Items')            setShowPicker(true);
                if (btn === 'Remove Items')         removeSelected();
                if (btn === 'Select/Deselect All')  selectDeselectAll();
              }}
              className="px-3 py-1.5 text-2xs font-semibold border border-sap-border rounded-lg
                         hover:bg-sap-blueLight hover:border-sap-blue text-sap-text transition-all
                         disabled:opacity-35 disabled:cursor-not-allowed text-left">
              {btn}
            </button>
          ))}
        </div>
      </div>

      {/* ── Add Items Picker Modal ── */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[80vh] flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b border-sap-border bg-sap-bg flex items-center justify-between">
              <p className="text-sm font-bold text-sap-text">Add Items</p>
              <button onClick={() => { setShowPicker(false); setSearch(''); setPickerSel([]); }}
                className="text-sap-textLight hover:text-sap-text text-lg leading-none">&times;</button>
            </div>
            <div className="px-5 py-3 border-b border-sap-border">
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by item no. or description..."
                className="w-full h-8 px-3 text-xs border border-sap-border rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingItems ? (
                <div className="py-10 text-center text-xs text-sap-textLight">Loading items…</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-sap-bg sticky top-0 border-b border-sap-border">
                    <tr>
                      <th className="w-8 px-2 py-2"></th>
                      <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Item No.</th>
                      <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-6 text-center text-sap-textLight italic">No items found.</td></tr>
                    ) : filtered.map(item => (
                      <tr key={item.item_id}
                        onClick={() => togglePickerRow(item.item_id)}
                        className={`border-t border-sap-border/30 cursor-pointer transition-colors
                          ${pickerSel.includes(item.item_id) ? 'bg-sap-blueLight' : 'hover:bg-sap-bg'}`}>
                        <td className="px-2 py-2 text-center">
                          <input type="checkbox" readOnly
                            checked={pickerSel.includes(item.item_id)}
                            className="w-3.5 h-3.5 accent-sap-blue pointer-events-none" />
                        </td>
                        <td className="px-3 py-2 text-sap-blue font-medium">{item.item_no}</td>
                        <td className="px-3 py-2 text-sap-text">{item.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-5 py-3 border-t border-sap-border bg-sap-bg flex items-center justify-between">
              <span className="text-2xs text-sap-textLight">{pickerSel.length} item{pickerSel.length !== 1 ? 's' : ''} selected</span>
              <div className="flex gap-2">
                <button onClick={() => { setShowPicker(false); setSearch(''); setPickerSel([]); }}
                  className="px-4 py-1.5 text-xs border border-sap-border rounded-lg hover:bg-white text-sap-text">Cancel</button>
                <button onClick={addSelected} disabled={pickerSel.length === 0}
                  className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded-lg
                             hover:bg-sap-blueDark disabled:opacity-40">
                  Add Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepDataSource({ dataSource, setDataSource }) {
  const [collapsed, setCollapsed] = useState({});   // location key → true means collapsed
  const [loading, setLoading]     = useState(false);

  /* Load warehouses on mount (preserve existing selections if returning to step) */
  useEffect(() => {
    if (dataSource.warehouses && dataSource.warehouses.length > 0) {
      return;
    }
    setLoading(true);
    api.get('/mrp/warehouses')
      .then(rows => {
        // The ticks this order was run with last time, remembered when the scenario
        // was chosen. They arrive before the warehouse list does, so they are applied
        // here rather than there.
        const saved = dataSource.savedWarehouses;
        const inventory = new Set((saved?.inventory || []).map(Number));
        const production = new Set((saved?.production || []).map(Number));
        const inScope = new Set((saved?.inScope || []).map(Number));
        const whs = filterMrpWarehouseList(rows || []).map(w => {
          const id = Number(w.warehouse_id ?? w.wh_id);
          return {
            ...w,
            warehouse_id: w.warehouse_id ?? w.wh_id,
            location: resolveMrpWarehouseDisplayLocation(w) || '',
            inclInventory: inventory.size ? inventory.has(id) : true,
            inclDemand:    inScope.size ? inScope.has(id) || inventory.has(id) : true,
            inclSupply:    inScope.size ? inScope.has(id) || inventory.has(id) : true,
            inclProduction: production.size ? production.has(id) : true,
          };
        });
        setDataSource(ds => ({ ...ds, warehouses: whs }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);  // runs once on mount

  const toggle = (warehouse_id, field) => {
    setDataSource(ds => ({
      ...ds,
      warehouses: ds.warehouses.map(w =>
        w.warehouse_id === warehouse_id ? { ...w, [field]: !w[field] } : w
      ),
    }));
  };

  /* Toggle entire row (all 3 checkboxes) */
  const toggleRow = (warehouse_id) => {
    setDataSource(ds => ({
      ...ds,
      warehouses: ds.warehouses.map(w => {
        if (w.warehouse_id !== warehouse_id) return w;
        const allOn = w.inclInventory && w.inclDemand && w.inclSupply && w.inclProduction;
        return { ...w, inclInventory: !allOn, inclDemand: !allOn, inclSupply: !allOn, inclProduction: !allOn };
      }),
    }));
  };

  /* Toggle all rows on/off */
  const toggleAllRows = () => {
    setDataSource(ds => {
      const visible = getDisplayRows(ds.warehouses, ds.runBy);
      const allOn = visible.every(w => w.inclInventory && w.inclDemand && w.inclSupply && w.inclProduction);
      return {
        ...ds,
        warehouses: ds.warehouses.map(w => {
          if (visible.find(v => v.warehouse_id === w.warehouse_id)) {
            return { ...w, inclInventory: !allOn, inclDemand: !allOn, inclSupply: !allOn, inclProduction: !allOn };
          }
          return w;
        }),
      };
    });
  };

  /* Toggle all checkboxes in a column */
  const toggleColumn = (field) => {
    setDataSource(ds => {
      const visible = getDisplayRows(ds.warehouses, ds.runBy);
      const allChecked = visible.every(w => w[field]);
      return {
        ...ds,
        warehouses: ds.warehouses.map(w => {
          if (visible.find(v => v.warehouse_id === w.warehouse_id)) {
            return { ...w, [field]: !allChecked };
          }
          return w;
        }),
      };
    });
  };

  /* Build display rows: Company mode shows one consolidated row pointing at first warehouse's state */
  const getDisplayRows = (whs, runBy) => {
    if (runBy === 'Company') {
      if (whs.length === 0) return [];
      const defaultWh = whs[0];
      return [{
        ...defaultWh,
        _isCompanyRow: true,
        location: 'Company',
        wh_name: 'All Warehouses (Consolidated)',
      }];
    }
    return whs;
  };

  const visibleWarehouses = getDisplayRows(dataSource.warehouses, dataSource.runBy);

  /* Group warehouses by location */
  const groups = visibleWarehouses.reduce((acc, w) => {
    const loc = w.location || 'Default';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(w);
    return acc;
  }, {});
  const locations = Object.keys(groups).sort();

  const expandAll  = () => setCollapsed({});
  const collapseAll = () => {
    const c = {};
    locations.forEach(l => { c[l] = true; });
    setCollapsed(c);
  };

  return (
    <div>
      <div className="mb-4">
        <p className="page-title">Inventory Data Source</p>
        <p className="text-xs text-sap-textLight mt-0.5">
          Define which storage locations and warehouses, as well as which sources of demand
          and supply to include in the MRP run.
        </p>
      </div>

      {/* Run By */}
      <div className="mb-4">
        <p className="text-xs font-semibold text-sap-text underline mb-2">Run By</p>
        <div className="space-y-1.5">
          {[
            { val: 'Company',   label: 'Company – Consolidate existing inventory, demand, and supply into default warehouse only.' },
            { val: 'Warehouse', label: 'Warehouse – Include existing inventory, demand, and supply separately for each warehouse.' },
          ].map(opt => (
            <label key={opt.val} className="flex items-start gap-2 text-xs text-sap-text cursor-pointer">
              <input type="radio" name="runBy" value={opt.val}
                checked={dataSource.runBy === opt.val}
                onChange={() => setDataSource(ds => ({ ...ds, runBy: opt.val }))}
                className="mt-0.5 accent-sap-blue w-3.5 h-3.5 shrink-0" />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Include Data Source table */}
      <div className="mb-2">
        <p className="text-xs font-semibold text-sap-text underline mb-2">Include Data Source</p>
      </div>
      <div className="flex gap-3">
        {/* Table */}
        <div className="flex-1 border border-sap-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-8 text-center text-xs text-sap-textLight">Loading warehouses…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-sap-bg border-b border-sap-border">
                <tr>
                  <th className="w-8 px-2 py-2 text-center">
                    <input type="checkbox"
                      checked={visibleWarehouses.length > 0 && visibleWarehouses.every(w => w.inclInventory && w.inclDemand && w.inclSupply && w.inclProduction)}
                      onChange={toggleAllRows}
                      className="w-3.5 h-3.5 accent-sap-blue" />
                  </th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-32 cursor-pointer select-none hover:text-sap-blue"
                    onDoubleClick={() => toggleColumn('inclInventory')}
                    title="Double-click to toggle all">Include Existing Inventory</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-28 cursor-pointer select-none hover:text-sap-blue"
                    onDoubleClick={() => toggleColumn('inclDemand')}
                    title="Double-click to toggle all">Include Demand</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-28 cursor-pointer select-none hover:text-sap-blue"
                    onDoubleClick={() => toggleColumn('inclSupply')}
                    title="Double-click to toggle all">Include Supply</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-28 cursor-pointer select-none hover:text-sap-blue"
                    onDoubleClick={() => toggleColumn('inclProduction')}
                    title="Open production order quantity, whole or part-finished, counts as supply here. Double-click to toggle all">
                    Include Production Order</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-24">Location</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-20">Wh. Code</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Warehouse Name</th>
                </tr>
              </thead>
              <tbody>
                {visibleWarehouses.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-sap-textLight italic">No warehouses found.</td>
                  </tr>
                ) : locations.map(loc => (
                  <React.Fragment key={loc}>
                    {/* Location group header */}
                    <tr className="bg-[#F0F4F3] border-t border-sap-border/40">
                      <td colSpan={8} className="px-3 py-1.5">
                        <button
                          onClick={() => setCollapsed(c => ({ ...c, [loc]: !c[loc] }))}
                          className="flex items-center gap-1.5 text-2xs font-semibold text-sap-blue hover:underline">
                          <span className="text-base leading-none">{collapsed[loc] ? '▶' : '▼'}</span>
                          {loc}
                        </button>
                      </td>
                    </tr>
                    {/* Warehouse rows */}
                    {!collapsed[loc] && groups[loc].map(w => (
                      <tr key={w.warehouse_id}
                        onClick={() => toggleRow(w.warehouse_id)}
                        className={`border-t border-sap-border/30 cursor-pointer transition-colors
                          ${w.inclInventory && w.inclDemand && w.inclSupply && w.inclProduction ? 'bg-sap-blueLight' : ''}
                          hover:bg-sap-blueLight/60`}>
                        <td className="px-2 py-2 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                            checked={w.inclInventory && w.inclDemand && w.inclSupply && w.inclProduction}
                            onChange={() => toggleRow(w.warehouse_id)}
                            className="w-3.5 h-3.5 accent-sap-blue" />
                        </td>
                        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={w.inclInventory}
                            onChange={() => toggle(w.warehouse_id, 'inclInventory')}
                            className="w-4 h-4 accent-sap-blue" />
                        </td>
                        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={w.inclDemand}
                            onChange={() => toggle(w.warehouse_id, 'inclDemand')}
                            className="w-4 h-4 accent-sap-blue" />
                        </td>
                        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={w.inclSupply}
                            onChange={() => toggle(w.warehouse_id, 'inclSupply')}
                            className="w-4 h-4 accent-sap-blue" />
                        </td>
                        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={w.inclProduction}
                            onChange={() => toggle(w.warehouse_id, 'inclProduction')}
                            className="w-4 h-4 accent-sap-blue" />
                        </td>
                        <td className="px-3 py-2 text-sap-textLight">
                          <span className="flex items-center gap-1">
                            <span className="text-sap-blue">→</span> {w.location || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium text-sap-blue">{w.wh_code}</td>
                        <td className="px-3 py-2 text-sap-text">{w.wh_name}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Side buttons */}
        <div className="flex flex-col gap-2 w-28 shrink-0">
          <button onClick={expandAll}
            className="px-3 py-1.5 text-2xs font-semibold border border-sap-border rounded-lg
                       hover:bg-sap-blueLight hover:border-sap-blue text-sap-text transition-all">
            Expand
          </button>
          <button onClick={collapseAll}
            className="px-3 py-1.5 text-2xs font-semibold border border-sap-border rounded-lg
                       hover:bg-sap-blueLight hover:border-sap-blue text-sap-text transition-all">
            Collapse
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Documents Data Source (Step 5) ── */
const DOC_SOURCES = [
  { key: 'purchaseRequests',          label: 'Purchase Requests',          rKey: 'restrictPurchaseRequests',     rLabel: 'Restrict Purchase Requests' },
  { key: 'purchaseQuotations',        label: 'Purchase Quotations',        rKey: 'restrictPurchaseQuotations',   rLabel: 'Restrict Purchase Quotations' },
  { key: 'purchaseOrders',            label: 'Purchase Orders',            rKey: 'restrictPurchaseOrders',       rLabel: 'Restrict Purchase Orders' },
  { key: 'blanketPurchaseAgreements', label: 'Blanket Purchase Agreements',rKey: 'restrictPurchaseAgreements',   rLabel: 'Restrict Purchase Agreements' },
  { key: 'salesQuotations',           label: 'Sales Quotations',           rKey: 'restrictSalesQuotations',      rLabel: 'Restrict Sales Quotations' },
  { key: 'salesOrders',               label: 'Sales Orders',               rKey: 'restrictSalesOrders',          rLabel: 'Restrict Sales Orders' },
  { key: 'blanketSalesAgreements',    label: 'Blanket Sales Agreements',   rKey: 'restrictSalesAgreements',      rLabel: 'Restrict Sales Agreements' },
  { key: 'productionOrders',          label: 'Production Orders',          rKey: 'restrictProductionOrders',     rLabel: 'Restrict Production Orders' },
  { key: 'inventoryTransferRequests', label: 'Inventory Transfer Requests',rKey: 'restrictTransferRequests',     rLabel: 'Restrict Transfer Requests' },
  { key: 'recurringOrderTransactions',label: 'Recurring Order Transactions',rKey: null,                          rLabel: null },
  { key: 'reserveInvoices',           label: 'Reserve Invoices',           rKey: 'restrictReserveInvoices',      rLabel: 'Restrict Reserve Invoices' },
];

function StepDocSource({ docSource, setDocSource, running, error, onRun }) {
  const s = (k, v) => setDocSource(d => ({ ...d, [k]: v }));
  const [openRestrict, setOpenRestrict] = useState(null); // which rKey popover is open

  /* Toggle a source checkbox — auto-clear restrict + dates when unchecking */
  const tog = (key) => {
    setDocSource(d => {
      const next = { ...d, [key]: !d[key] };
      // If unchecking a source, also clear its restrict checkbox
      const doc = DOC_SOURCES.find(ds => ds.key === key);
      if (doc && doc.rKey && !next[key]) {
        next[doc.rKey] = false;
        // clear restrict dates too
        const rd = { ...(next.restrictDates || {}) };
        delete rd[doc.rKey];
        next.restrictDates = rd;
      }
      return next;
    });
  };

  /* Toggle a restrict checkbox — clear dates when unchecking */
  const togRestrict = (rKey) => {
    setDocSource(d => {
      const next = { ...d, [rKey]: !d[rKey] };
      if (!next[rKey]) {
        const rd = { ...(next.restrictDates || {}) };
        delete rd[rKey];
        next.restrictDates = rd;
        setOpenRestrict(null);
      }
      return next;
    });
  };

  /* Set restrict date range for a key */
  const setRestrictDate = (rKey, field, value) => {
    setDocSource(d => ({
      ...d,
      restrictDates: {
        ...(d.restrictDates || {}),
        [rKey]: { ...(d.restrictDates?.[rKey] || {}), [field]: value },
      },
    }));
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="page-title">Documents Data Source</p>
        <p className="text-xs text-sap-textLight mt-0.5">
          Define the data sources as well as the documents to include in the MRP calculation.
        </p>
      </div>

      {/* ── Time Range ── */}
      <div>
        <p className="text-xs font-semibold text-sap-text underline mb-2">Time Range</p>
        <div className="space-y-1.5">
          {['Within Planning Horizon', 'Include Historical Data'].map(opt => (
            <label key={opt} className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
              <input type="radio" name="timeRange" value={opt}
                checked={docSource.timeRange === opt}
                onChange={() => s('timeRange', opt)}
                className="w-3.5 h-3.5 accent-sap-blue shrink-0" />
              {opt}
            </label>
          ))}
        </div>
      </div>

      {/* ── Sources of Demand & Supply ── */}
      <div>
        <p className="text-xs font-semibold text-sap-text underline mb-2">
          Sources of Demand and Supply to Be Included in MRP Calculation
        </p>
        <div className="border border-sap-border rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <tbody>
              {DOC_SOURCES.map(doc => (
                <tr key={doc.key} className="border-b border-sap-border/30 last:border-0 hover:bg-sap-bg/50">
                  {/* Include checkbox */}
                  <td className="px-3 py-2 w-1/2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!docSource[doc.key]}
                        onChange={() => tog(doc.key)}
                        className="w-3.5 h-3.5 accent-sap-blue shrink-0" />
                      <span className={!docSource[doc.key] ? 'text-sap-textLight' : ''}>{doc.label}</span>
                    </label>
                  </td>
                  {/* Restrict checkbox + "..." button + date popover */}
                  <td className="px-3 py-2 w-1/2">
                    {doc.rKey ? (
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 cursor-pointer flex-1">
                            <input type="checkbox" checked={!!docSource[doc.rKey]}
                              onChange={() => togRestrict(doc.rKey)}
                              disabled={!docSource[doc.key]}
                              className="w-3.5 h-3.5 accent-sap-blue shrink-0 disabled:opacity-40" />
                            <span className={!docSource[doc.key] ? 'text-sap-textLight' : ''}>{doc.rLabel}</span>
                          </label>
                          <button
                            disabled={!docSource[doc.rKey]}
                            onClick={() => setOpenRestrict(openRestrict === doc.rKey ? null : doc.rKey)}
                            className={`px-2 py-0.5 text-2xs border rounded shrink-0 transition-colors
                              ${openRestrict === doc.rKey ? 'border-sap-blue bg-sap-blueLight text-sap-blue' : 'border-sap-border hover:bg-sap-blueLight'}
                              disabled:opacity-30`}>
                            …
                          </button>
                        </div>
                        {/* Restrict date-range popover */}
                        {openRestrict === doc.rKey && (
                          <div className="absolute right-0 top-8 z-30 bg-white border border-sap-border rounded-lg shadow-lg p-3 w-64">
                            <p className="text-2xs font-semibold text-sap-text mb-2">Date Range: {doc.rLabel}</p>
                            <div className="flex items-center gap-2 mb-2">
                              <label className="text-2xs text-sap-textLight w-10">From</label>
                              <input type="date"
                                value={docSource.restrictDates?.[doc.rKey]?.from || ''}
                                onChange={e => setRestrictDate(doc.rKey, 'from', e.target.value)}
                                className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                              <label className="text-2xs text-sap-textLight w-10">To</label>
                              <input type="date"
                                value={docSource.restrictDates?.[doc.rKey]?.to || ''}
                                onChange={e => setRestrictDate(doc.rKey, 'to', e.target.value)}
                                className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
                            </div>
                            <div className="flex justify-end gap-2">
                              <button onClick={() => { setRestrictDate(doc.rKey, 'from', ''); setRestrictDate(doc.rKey, 'to', ''); }}
                                className="px-2 py-1 text-2xs border border-sap-border rounded hover:bg-sap-bg">Clear</button>
                              <button onClick={() => setOpenRestrict(null)}
                                className="px-3 py-1 text-2xs bg-sap-blue text-white rounded hover:bg-sap-blueDark">OK</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Inventory Level & Forecast ── */}
      <div className="flex flex-wrap gap-6">
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-sap-text w-28 shrink-0">Inventory Level</label>
          <select value={docSource.inventoryLevel} onChange={e => s('inventoryLevel', e.target.value)}
            className="h-8 px-2 text-xs border border-sap-border rounded-lg bg-white w-48
                       focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
            <option value="">— None —</option>
            <option>Item Cost</option>
            <option>Moving Average</option>
            <option>Price List 01</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs font-medium text-sap-text w-16 shrink-0">Forecast</label>
          <select value={docSource.forecast} onChange={e => s('forecast', e.target.value)}
            className="h-8 px-2 text-xs border border-sap-border rounded-lg bg-white w-48
                       focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
            <option value="">— None —</option>
            <option>Use Forecast</option>
            <option>Ignore Forecast</option>
          </select>
        </div>
      </div>

      {/* ── Recommendations ── */}
      <div>
        <p className="text-xs font-semibold text-sap-text underline mb-3">Recommendations</p>
        <div className="space-y-4 pl-2">
          {/* Purchase */}
          <div>
            {/* Always on, as in SAP's wizard, where this box is greyed out. Unticking it
                meant no purchase recommendations at all — every Buy item silently dropped
                from the run — and on a screen that says "Production Orders" twice, testers
                reasonably untick the wrong one. */}
            <label className="flex items-center gap-2 text-xs font-semibold text-sap-textLight mb-2">
              <input type="checkbox" checked disabled
                className="w-3.5 h-3.5 accent-sap-blue opacity-60" />
              Purchase
            </label>
            {(
              <div className="pl-5 grid grid-cols-2 gap-x-8 gap-y-1.5">
                {/* Type */}
                <div className="space-y-1.5">
                  {['Purchase Requests', 'Purchase Orders'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
                      <input type="radio" name="purchaseRecType" value={opt}
                        checked={docSource.purchaseRecType === opt}
                        onChange={() => s('purchaseRecType', opt)}
                        className="w-3.5 h-3.5 accent-sap-blue shrink-0" />
                      {opt}
                    </label>
                  ))}
                </div>
                {/* Warehouse */}
                <div className="space-y-1.5">
                  {['Generate to Default Warehouse for Item', 'Generate to Warehouse with the Demand'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
                      <input type="radio" name="purchaseWarehouse" value={opt}
                        checked={docSource.purchaseWarehouse === opt}
                        onChange={() => s('purchaseWarehouse', opt)}
                        className="w-3.5 h-3.5 accent-sap-blue shrink-0" />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Production Orders */}
          <div>
            {/* Always on, like Purchase above and like SAP. */}
            <label className="flex items-center gap-2 text-xs font-semibold text-sap-textLight mb-2">
              <input type="checkbox" checked disabled
                className="w-3.5 h-3.5 accent-sap-blue opacity-60" />
              Production Orders
            </label>
          </div>
          {/* Its own option, not a child of Production Orders. It used to be nested
              there, so unticking Production Orders hid it while its value was still
              sent — a setting that shaped ~350 lines of the run, invisible. */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-sap-textLight cursor-pointer">
              <input type="checkbox" checked={docSource.recInventoryTransfers}
                onChange={() => tog('recInventoryTransfers')}
                className="w-3.5 h-3.5 accent-sap-blue" />
              Inventory Transfer Requests
            </label>
          </div>
        </div>
      </div>

      {/* ── Run button at bottom of step 5 ── */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-xs text-red-600">
          {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-sap-border">
        {/* The server re-reads stock from SAP for the run's items first, so a run
            takes a minute or two longer than before. Saying so stops people
            clicking again or leaving the page. */}
        {running && (
          <span className="text-xs text-sap-textLight">
            Refreshing stock from SAP, then planning — this can take 1–3 minutes.
          </span>
        )}
        <button onClick={onRun} disabled={running}
          className="flex items-center gap-2 px-6 py-2 text-xs font-semibold bg-amber-500
                     text-white rounded-lg hover:bg-amber-600 disabled:opacity-40 shadow-sm transition-all">
          {running && (
            <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          {running ? 'Running MRP…' : '▶  Run MRP'}
        </button>
      </div>
    </div>
  );
}


function StepResults({ result, form, details }) {
  const [activeTab, setActiveTab]       = useState('Report');
  const [previewRun, setPreviewRun]     = useState(true);
  const [filterProblems, setFilterProblems] = useState(false);
  const [searchItem, setSearchItem]     = useState('');
  const [recs, setRecs]                 = useState([]);
  const [loadingRecs, setLoadingRecs]   = useState(false);
  const [expanded, setExpanded]         = useState(true);

  useEffect(() => {
    if (!result?.run_id) return;
    setLoadingRecs(true);
    api.get(`/mrp/recommendations/${result.run_id}`)
      .then(d => setRecs(d.recommendations || []))
      .catch(() => setRecs([]))
      .finally(() => setLoadingRecs(false));
  }, [result]);

  if (!result) return (
    <div className="flex justify-center py-10 text-xs text-sap-textLight">No results yet.</div>
  );

  const filteredRecs = recs.filter(r =>
    !searchItem ||
    r.item_no?.toLowerCase().includes(searchItem.toLowerCase()) ||
    r.item_desc?.toLowerCase().includes(searchItem.toLowerCase())
  );

  const handleExportExcel = () => {
    if (!recs || recs.length === 0) {
      alert("No recommendations to export.");
      return;
    }

    const wb = XLSX.utils.book_new();

    // Group by doc_type
    const groups = {};
    recs.forEach(r => {
      const type = r.doc_type || 'Other';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(r);
    });

    // For each doc_type, prepare data and sheet
    Object.keys(groups).forEach(docType => {
      const items = groups[docType];
      let sheetData = [];

      if (docType === 'Purchase Order' || docType === 'Purchase Quotation' || docType === 'Purchase Request') {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Required Qty': Number(r.recommended_qty) || 0,
          // Purchasing documents are raised in the item's PURCHASE unit, which is
          // what SAP shows (CO_27_0001 is bought in KG, EL_01_0017 in MTR) even
          // though the inventory unit is NOS. r.uom is items.purchase_uom.
          'UoM': r.uom || r.uom_name || 'Nos',
          'Required Date': r.required_date || '',
          'Unit Price (INR)': Number(r.unit_price) || 0,
          'Total Value (INR)': (Number(r.recommended_qty) || 0) * (Number(r.unit_price) || 0),
          'Preferred Vendor': r.vendor_name ? `${r.vendor_name} (${r.vendor_code || r.preferred_vendor})` : (r.preferred_vendor || ''),
          'Lead Time (Days)': r.lead_time_days || 0,
          'Project Code': r.inherited_project_no || '',
          'Distribution Rule': r.inherited_distr_rule || '',
          'Status': r.status || ''
        }));
      } else if (docType === 'Production Order') {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Required Qty': Number(r.recommended_qty) || 0,
          'UoM': r.uom_name || r.uom || 'Nos',
          'Required Date': r.required_date || '',
          'Procurement Method': r.procurement_method || 'Make',
          'Status': r.status || ''
        }));
      } else if (docType === 'Inventory Transfer') {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Required Qty': Number(r.recommended_qty) || 0,
          'UoM': r.uom_name || r.uom || 'Nos',
          'Required Date': r.required_date || '',
          'From Warehouse': r.from_wh_name ? `${r.from_wh_name} (${r.from_wh_code})` : (r.from_wh_code || ''),
          'To Warehouse': r.to_wh_name ? `${r.to_wh_name} (${r.to_wh_code})` : (r.to_wh_code || ''),
          'Available at Source': Number(r.from_wh_available) || 0,
          'Status': r.status || ''
        }));
      } else {
        sheetData = items.map((r, index) => ({
          'S.No.': index + 1,
          'Item Code': r.item_no || '',
          'Description': r.item_desc || '',
          'Required Qty': Number(r.recommended_qty) || 0,
          'UoM': r.uom_name || r.uom || 'Nos',
          'Required Date': r.required_date || '',
          'Doc Type': r.doc_type || '',
          'Procurement Method': r.procurement_method || '',
          'Status': r.status || ''
        }));
      }

      const ws = XLSX.utils.json_to_sheet(sheetData);

      // Set column widths to prevent clipping
      const cols = [];
      if (sheetData.length > 0) {
        Object.keys(sheetData[0]).forEach(key => {
          let maxLen = key.toString().length;
          sheetData.forEach(row => {
            const val = row[key] ? row[key].toString() : '';
            if (val.length > maxLen) maxLen = val.length;
          });
          cols.push({ wch: Math.min(Math.max(maxLen + 2, 8), 50) });
        });
        ws['!cols'] = cols;
      }

      const cleanSheetName = docType.substring(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, cleanSheetName);
    });

    const dateStr = new Date().toISOString().slice(0, 10);
    const fileName = `MRP_Recommendations_Run_${result.run_id}_${dateStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const now = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-bold text-sap-text">MRP Results</p>
          <p className="text-xs text-sap-textLight mt-0.5">
            On the "Report" tab, you may toggle the final inventory quantity either before or after
            the MRP run with the "Preview MRP Run Results" checkbox.
          </p>
        </div>
        <div className="text-right text-2xs text-sap-textLight shrink-0">
          <div>Planning Horizon &nbsp;
            <span className="font-medium text-sap-text">{form.startDate} – {form.endDate}</span>
          </div>
          <div>Calculated At &nbsp;<span className="font-medium text-sap-text">{now}</span></div>
        </div>
      </div>

      {/* Find Item */}
      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-sap-text shrink-0">Find Item No.</label>
        <input value={searchItem} onChange={e => setSearchItem(e.target.value)}
          placeholder="Search item no. or description…"
          className="w-64 h-7 px-2 text-xs border border-sap-border rounded bg-[#FFFFF0]
                     focus:outline-none focus:border-sap-blue" />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-sap-border gap-1">
        {['Report', 'Recommendations'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-t transition-colors
              ${activeTab === tab
                ? 'bg-white border border-b-white border-sap-border text-sap-blue -mb-px'
                : 'text-sap-textLight hover:text-sap-text'}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Tab options row */}
      <div className="flex items-center gap-6">
        {activeTab === 'Report' && (
          <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
            <input type="checkbox" checked={previewRun} onChange={e => setPreviewRun(e.target.checked)}
              className="w-3.5 h-3.5 accent-sap-blue" />
            Preview MRP Run Results
          </label>
        )}
        <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
          <input type="checkbox" checked={filterProblems} onChange={e => setFilterProblems(e.target.checked)}
            className="w-3.5 h-3.5 accent-sap-blue" />
          Filter Possible Problem Items
        </label>
      </div>

      {/* ── Report Tab ── */}
      {activeTab === 'Report' && (
        <div className="border border-sap-border rounded-xl overflow-auto max-h-80">
          {loadingRecs ? (
            <div className="py-8 text-center text-xs text-sap-textLight">Loading…</div>
          ) : (
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-sap-bg sticky top-0 border-b border-sap-border">
                <tr>
                  <th className="px-2 py-2 text-sap-textLight font-semibold w-8">#</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-28">Item No.</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Item Description</th>
                  <th className="px-3 py-2 text-right text-sap-textLight font-semibold w-24">Required Qty</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-28">Required Date</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-32">Doc Type</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-24">Proc.</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold w-20">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-sap-textLight italic">
                      {recs.length === 0
                        ? 'No recommendations for this run.'
                        : 'No items match the search.'}
                    </td>
                  </tr>
                ) : filteredRecs.map((r, i) => (
                  <tr key={r.rec_id}
                    className={`border-t border-sap-border/30 ${r.status === 'Released' ? 'bg-[#EDF5F0]' : 'hover:bg-sap-bg'}`}>
                    <td className="px-2 py-2 text-center text-sap-textLight">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-sap-blue">{r.item_no}</td>
                    <td className="px-3 py-2 text-sap-text">{r.item_desc}</td>
                    <td className="px-3 py-2 text-right font-medium text-sap-text">
                      {Number(r.recommended_qty).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-2 text-sap-text">{r.required_date}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-2xs font-medium
                        ${r.doc_type === 'Purchase Order' ? 'bg-sap-blueMuted text-sap-blueDeeper' : 'bg-[#FFF3E0] text-[#9C6B00]'}`}>
                        {r.doc_type}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-2xs font-medium
                        ${r.procurement_method === 'Buy' ? 'bg-sap-blueMuted text-sap-blueDeeper' : 'bg-[#FFF3E0] text-[#9C6B00]'}`}>
                        {r.procurement_method}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-2xs font-medium
                        ${r.status === 'Released' ? 'bg-[#EDF5F0] text-[#4A9E6E]'
                          : r.status === 'Accepted' ? 'bg-sap-blueMuted text-sap-blueDeeper'
                          : 'bg-[#F5F0FF] text-[#7B6FA0]'}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Recommendations Tab ── */}
      {activeTab === 'Recommendations' && (
        <div className="border border-sap-border rounded-xl overflow-auto max-h-80">
          {loadingRecs ? (
            <div className="py-8 text-center text-xs text-sap-textLight">Loading…</div>
          ) : (
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-sap-bg sticky top-0 border-b border-sap-border">
                <tr>
                  <th className="px-2 py-2 text-sap-textLight font-semibold w-8">#</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Item No.</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Item Description</th>
                  <th className="px-3 py-2 text-right text-sap-textLight font-semibold">Qty</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Required Date</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Doc Type</th>
                  <th className="px-3 py-2 text-left text-sap-textLight font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-sap-textLight italic">
                      No recommendations for this run.
                    </td>
                  </tr>
                ) : filteredRecs.map((r, i) => (
                  <tr key={r.rec_id} className="border-t border-sap-border/30 hover:bg-sap-bg">
                    <td className="px-2 py-2 text-center text-sap-textLight">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-sap-blue">{r.item_no}</td>
                    <td className="px-3 py-2 text-sap-text">{r.item_desc}</td>
                    <td className="px-3 py-2 text-right font-medium">{Number(r.recommended_qty).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-sap-text">{r.required_date}</td>
                    <td className="px-3 py-2 text-sap-text">{r.doc_type}</td>
                    <td className="px-3 py-2">
                      {r.status === 'Planned' ? (
                        <AcceptButton recId={r.rec_id} onAccepted={() =>
                          setRecs(prev => prev.map(x => x.rec_id === r.rec_id ? { ...x, status: 'Released' } : x))
                        } />
                      ) : (
                        <span className="text-2xs text-[#4A9E6E] font-semibold">✓ {r.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Bottom action bar */}
      <div className="flex items-center justify-between pt-2 border-t border-sap-border flex-wrap gap-2">
        <div className="flex gap-2">
          <button onClick={() => setExpanded(e => !e)}
            className="px-3 py-1.5 text-2xs font-semibold border border-sap-border rounded-lg
                       hover:bg-sap-blueLight text-sap-text transition-all">
            {expanded ? 'Collapse' : 'Expand'} / Collapse
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            className="px-4 py-1.5 text-2xs font-semibold bg-[#4A9E6E] text-white rounded-lg
                       hover:bg-[#3D8C5C] transition-all flex items-center gap-1.5 shadow-sm"
          >
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
            </svg>
            Export to Excel
          </button>
          <button
            onClick={() => {
              if (result.recommendations_generated > 0) {
                alert(`Run #${result.run_id}: ${result.recommendations_generated} recommendations saved.`);
              }
            }}
            className="px-4 py-1.5 text-2xs font-semibold bg-amber-500 text-white rounded-lg
                       hover:bg-amber-600 transition-all">
            Save Recommendations
          </button>
          <button
            className="px-4 py-1.5 text-2xs font-semibold border border-sap-border rounded-lg
                       hover:bg-sap-blueLight text-sap-text transition-all opacity-40 cursor-not-allowed">
            Save Scenario
          </button>
        </div>
      </div>

      {/* Summary pill */}
      <div className="flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#EDF5F0] rounded-lg text-[#4A9E6E] font-semibold">
          ✓ Run #{result.run_id} complete
        </span>
        <span className="text-sap-textLight">
          {result.recommendations_generated} recommendation{result.recommendations_generated !== 1 ? 's' : ''} generated
        </span>
        {/* Evidence of which stock the run planned on. A run that could not re-read
            SAP still completes, but on synced stock — worth knowing before comparing
            it with SAP's own run. */}
        {result.stock_refresh && (result.stock_refresh.done ? (
          <span className="text-sap-textLight">
            · Stock refreshed from SAP for {result.stock_refresh.items} items
            ({result.stock_refresh.rowsChanged} changed, {result.stock_refresh.seconds}s)
          </span>
        ) : (
          <span className="text-amber-700">
            · Stock not refreshed from SAP — {result.stock_refresh.reason}
          </span>
        ))}
      </div>
    </div>
  );
}

function AcceptButton({ recId, onAccepted }) {
  const [loading, setLoading] = useState(false);
  const accept = async () => {
    setLoading(true);
    try { await api.post('/mrp/accept', { rec_id: recId }); onAccepted(); }
    catch { /* silently fail */ }
    finally { setLoading(false); }
  };
  return (
    <button onClick={accept} disabled={loading}
      className="px-2 py-0.5 text-2xs font-semibold bg-sap-blue text-white rounded
                 hover:bg-sap-blueDark disabled:opacity-40 transition-all">
      {loading ? '…' : 'Accept'}
    </button>
  );
}

/* ─── Field components ──────────────────────────── */

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="flex items-center gap-3">
      <label className="w-44 text-xs font-medium text-sap-textLight text-right shrink-0">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="flex-1 h-9 px-3 text-xs border border-sap-border rounded-lg bg-white
                   focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue" />
    </div>
  );
}

function CheckField({ label, checked, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-44 shrink-0" />
      <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          className="w-4 h-4 rounded border-sap-border accent-sap-blue" />
        {label}
      </label>
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-3">
      <label className="w-44 text-xs font-medium text-sap-textLight text-right shrink-0">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="flex-1 h-9 px-3 text-xs border border-sap-border rounded-lg bg-white
                   focus:outline-none focus:ring-2 focus:ring-sap-blue/30 focus:border-sap-blue">
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

